import type { PluginToolsResponse } from '@palacete/shared/plugin-publication';
import type { MortgageEstimate } from '@palacete/shared/mortgage-recipe';
import type { PluginGovernance, PluginPolicy, PluginOwnerItem } from '@palacete/shared/plugins';
import type { PaletteInput } from '@palacete/shared/palette';
import type { BrandingTheme } from '@palacete/shared/branding';
import type { BriefConfig, BriefField } from '@palacete/shared/brief';
import type {
  Page, PageVersion, LeadIntentPage, MediaItem, MediaLibraryItem, MediaUploadContext,
  SiteSettings, User, PublishLog, FormSubmission, SubmissionStats, VisitStats,
  ContactListResponse, ContactDetailResponse, ContactRecord, ContactDuplicateCandidate, ContactStatusRecord, ContactJobMutation, ContactTimelineEntry, PendingItemRecord,
  OperationsListResponse,
  PendingItemTemplateRecord, PendingItemTemplateEntry, PendingItemTemplateRole,
  TemplateStageRecord, JobStageEventRecord, StageCategory, ResponsibleParty,
  ContactImportPreviewResponse, ContactImportResultResponse, ContactImportRow,
  ContactFollowupSettings, ContactFollowupResponse, PendingItemDigestSettings, ExpiryDateRecord,
  CollectionTypeDef, CollectionItem, OnboardingCatalog, OnboardingPromptSuggestion, ChecklistStatus, DetectedIdentity,
  OnboardingDraft, AcquisitionRedemption, AcquisitionFunnel,
  MarketingAssetRecord,
  BookingRecord, BookingReminderSettings, BookingSchedule, BookingService, BookingStaff, BookingStaffGroup,
  FeedbackRequestRecord, FeedbackResponseRecord, TestimonialRecord, FeedbackSummary, FeedbackSettingsRecord,
  SurveyCampaignListItem, SurveyCampaignDetail, SurveyResponseRecord, SurveyDeliverableRecord,
  SurveyVariantRecord, SurveyQuestionRecord, SurveyObjective, SurveyCampaignPurpose, SurveyHostMode,
  SurveyLeadStatus, SurveyCampaignSettings, SurveyQuestionType, SurveyOption, SurveyStatus, CascadeOptionsMap,
  QrCodeRecord,
  GoogleBusinessRecord, GooglePlaceCandidate, CitySuggestion,
  ChatbotSettingsRecord, ChatbotSettingsInput, ChatbotUsageSummary,
  ChatbotConversationSummary, ChatbotMessageRecord,
  CloudflareDomainStatus, DomainSearchResult, DomainCheckResult, DomainConnectResult,
  RedirectPresetState, TutorialProgressState,
  LegalDoc, LegalDocKey, LegalDocLocale,
  LinksResponse, LinksLocaleState, BioProps,
  TenantOperation,
  AnnouncementRecord, AnnouncementInput,
  AssistantEventInput, ActivityEntry, AssistantExecuteResult,
  AiImageGenerateInput, AiImageGenerateResult,
  BusinessProfileRecord, BusinessProfileResponse, BusinessIdentityRecord, BusinessIdentityPatch, BusinessAttributeRecord,
  PresenceScoreResponse,
  TrackedPromptRecord, TrackedCompetitorRecord, PromptStatus, CompetitorStatus,
  VisibilityRunRecord, VisibilityRunDetail, VisibilityObservationRecord, VisibilityCitationRecord,
  VisibilityPlatform, RunType, CitationInput, VisibilityMentionLocation, VisibilityResponseStage, VisibilityAccuracy, VisibilityAccuracyIssueType,
  VisibilitySeedCatalogItem, VisibilitySeedDetail, VisibilitySeedResult, VisibilityDashboard,
  SessionUser, SignupAccessEntry, SignupGateState, SetupStatus, TenantSummary, TenantType,
  PlatformWaitlistCampaignOption, PlatformWaitlistPreview,
  PlatformPublishLogEntry,
} from './types';
import { emitSessionExpired } from './sessionEvents';
import type { AiSettings, AiPrompts, AiPricing } from '@palacete/shared/ai-settings';

/** SRV-03 — estado de las preguntas del formulario: lo guardado, lo sugerido por el giro y lo que pinta el sitio hoy. */
export interface BriefSettingsResponse {
  brief: BriefConfig;
  industry: string | null;
  suggested: BriefField[];
  effective: BriefField[];
}
import type { SecuritySettings, DEFAULT_SECURITY } from '@palacete/shared/security-settings';
import type { LegalDoc as PlatformLegalDoc, LegalSettings } from '@palacete/shared/legal-settings';
import type { LegalProfile, LegalOperationalSettings, RetentionRule, LegalReadiness, LegalDrafts } from '@palacete/shared/legal-settings';
import type {
  LegalInventoryItem, LegalInventoryChange, LegalInventoryItemInput, LegalInventoryStatus,
} from '@palacete/shared/legal-inventory';
import type { ArcoRequest, ArcoRequestInput, ArcoRequestStatus, ArcoRequestType } from '@palacete/shared/arco';
import type { SecurityIncident, NotificationDecision } from '@palacete/shared/security-incidents';
import type { FiscalProfile, FiscalProfileInput } from '@palacete/shared/cfdi';
import type { VisitorRequest, VisitorRequestStatus } from '@palacete/shared/tenant-visitor-arco';
import type { SupportSettings } from '@palacete/shared/support';
import type { InfraSettings } from '@palacete/shared/infra-settings';
import type { PlatformCredentialField, PlatformCredentialsStatus } from '@palacete/shared/platform-credentials';
import type { CalculationDatasetRelease } from '@palacete/shared/calculation-datasets';
import type { SaleProceedsEstimate } from '@palacete/shared/sale-proceeds-recipe';
import type { PropertyComparable, PropertyMarketReference } from '@palacete/shared/property-valuation';

const BASE = '/api';

// Defaults de protección (los mismos DEFAULT_SECURITY del worker) que la UI usa
// como placeholders cuando un campo no está personalizado.
export type SecuritySettingsDefaults = typeof DEFAULT_SECURITY;

export interface SecuritySettingsResponse {
  settings: SecuritySettings;
  defaults: SecuritySettingsDefaults;
  hasTurnstileSecret: boolean;
}

export interface ComplianceWarning {
  id: 'privacy_notice_incomplete';
  severity: 'blocks_public_forms';
  title: string;
  hint: string;
  href: '/site?tab=legal';
}

// ── Ayuda y soporte (GOL-11) ────────────────────────────────────────────────
export type ResolvedSupportConfig = Required<SupportSettings> & { promise: string };

export interface SupportSettingsResponse {
  settings: SupportSettings;
  defaults: Required<SupportSettings>;
  resolved: ResolvedSupportConfig;
  hasAiKey: boolean;
}

/** Lo que la UI del cliente necesita para pintar la ayuda. */
export interface SupportPublicConfig {
  email: string;
  promise: string;
  responseHours: number;
  assistantEnabled: boolean;
}

export interface SupportAskTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface SupportAskResult {
  answer: string;
  resolved: boolean;
  articles: { id: string; question: string; to: string | null; toLabel: string | null }[];
}

// Valores efectivos por defecto (entorno + código) que el panel muestra como
// referencia/placeholder cuando el SaaS owner no ha personalizado un campo.
export interface AiSettingsDefaults {
  textModel: string;
  imageModel: string;
  imageEnabled: boolean;
  textDailyLimit: number;
  imageMonthlyLimit: number;
  imageDefaultSize: string;
  imageDefaultQuality: string;
  imageDefaultBackground: string;
  prompts: AiPrompts;
  industryStyleHints: Record<string, string>;
  industryCopyHints: Record<string, string>;
  pricing: AiPricing;
}

// OPS-05A — respuesta de /platform/costs.
export interface UnitCostRow {
  operation: string;
  label: string;
  calls: number;
  avgTokensIn: number;
  avgTokensOut: number;
  avgCpuMs: number;
  unitCostMXN: number;
}

export interface TenantCostRow {
  tenantId: string;
  tenantName: string;
  month: string;
  operations: {
    operation: string;
    label: string;
    calls: number;
    tokensIn: number;
    tokensOut: number;
    cpuMs: number;
    costMXN: number;
  }[];
  publishes: number;
  publishMinutes: number;
  publishCostMXN: number;
  totalMXN: number;
}

export interface PlatformCostsResponse {
  month: string;
  pricing: AiPricing;
  unitCosts: UnitCostRow[];
  tenants: TenantCostRow[];
  totalMXN: number;
}

export interface AiSettingsResponse {
  settings: AiSettings;
  defaults: AiSettingsDefaults;
  hasApiKey: boolean;
}

// SEC-09 — config de infraestructura (solo r2PublicUrl por ahora).
export interface InfraSettingsResponse {
  settings: InfraSettings;
  defaults: InfraSettings;
}

// SEC-08 fase 1 — credenciales de plataforma de bajo riesgo (escribir sí, mostrar no).
export interface PlatformCredentialsResponse extends PlatformCredentialsStatus {
  hasKey: boolean;
}

// ── Billing ──────────────────────────────────────────────────────────────────
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'unpaid' | 'canceled' | 'none';

export interface Entitlement {
  appUserId: string;
  active: boolean;
  status: SubscriptionStatus;
  trialDaysLeft: number | null;
  plan: string;
  features: Record<string, boolean>;
  gatewaySubscriptionId?: string | null;
}

/** Price comprobado directamente en Stripe para la modalidad mostrada. */
export interface LiveBillingPrice {
  id: string;
  unitAmount: number | null;
  currency: string;
  interval: 'month' | 'year';
}

/** Suscripción real del tenant en Stripe (GOL-01D): lo que paga de verdad, no el
 *  catálogo. `null` cuando no hay pago activo o no se pudo confirmar en vivo. */
export interface LiveSubscriptionDetails {
  gatewayPriceId: string;
  unitAmount: number | null;
  currency: string;
  interval: 'month' | 'year' | string;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
}

export interface EntitlementResponse {
  entitlement: Entitlement;
  /** `false` cuando no fue posible confirmar los Prices con Stripe. */
  pricingAvailable: boolean;
  /** Detalle en vivo de la suscripción activa/`past_due`; `null` fuera de esos
   *  estados o si no se pudo confirmar (el panel debe degradar, no romperse). */
  subscription: LiveSubscriptionDetails | null;
  gate: { signupPolicy: 'pro_trial_then_free' | 'free_from_start'; trialDays: number; mesesGratisAnual: number };
  plan: {
    id: string;
    price: LiveBillingPrice | null;
    annualPrice: LiveBillingPrice | null;
    features: Record<string, boolean>;
    limits: Record<string, number>;
    label: string;
    purchasable: boolean;
  };
  plans: Array<{
    id: string;
    price: LiveBillingPrice | null;
    annualPrice: LiveBillingPrice | null;
    features: Record<string, boolean>;
    limits: Record<string, number>;
    label: string;
    purchasable: boolean;
  }>;
}

// ── Facturas a nombre del cliente (GOL-10B) ─────────────────────────────────

/** Un cobro del tenant con su estado de facturación. Espejo de
 *  `InvoiceableCharge` en `worker/lib/fiscal-nominative.ts`. */
export interface InvoiceableCharge {
  id: string;
  period: string;
  occurredAt: number;
  amountTotal: number;
  base: number;
  iva: number;
  currency: string;
  status: string;
  cfdiUuid: string | null;
  downloadable: boolean;
  requestable: boolean;
  blockedReason: string | null;
  deadlineAt: number;
}

export interface TenantInvoicesResponse {
  profile: FiscalProfile | null;
  charges: InvoiceableCharge[];
  currentDeadlineAt: number;
}

/** Una solicitud de factura vista desde el panel del emisor (saas_admin). */
export interface InvoiceRequestState {
  id: string;
  recordId: string;
  tenantId: string;
  status: string;
  requestedByEmail: string | null;
  receptorRfc: string;
  receptorName: string;
  cfdiUuid: string | null;
  errorMessage: string | null;
  createdAt: number;
}

export interface PageDeleteBlockedResponse {
  error: string;
  blockingReason: 'historical_versions' | 'related_records';
  references: {
    pageVersions: number;
    pageEditorDrafts: number;
    previewDrafts: number;
    mediaGenerations: number;
  };
}

export interface BillingGateState {
  signupPolicy: 'pro_trial_then_free' | 'free_from_start';
  trialDays: number;
  decommissionMonths: number;
  mesesGratisAnual: number;
}

export interface PlanDefinitionState {
  active?: boolean;
  features: Record<string, boolean>;
  /** Topes numéricos del plan. `-1` sin tope, `0` capacidad apagada. */
  limits?: Record<string, number>;
  refs: Partial<Record<'stripe' | 'conekta' | 'openpay', string>>;
  annualRefs?: Partial<Record<'stripe' | 'conekta' | 'openpay', string>>;
  lookupKeys?: { month?: string; year?: string };
}

export type AddonBillingState = 'one_time' | 'annual' | 'monthly';

export interface AddonDefinitionState {
  key: string;
  label: string;
  active?: boolean;
  billing: AddonBillingState;
  refs: Partial<Record<'stripe' | 'conekta' | 'openpay', string>>;
  cfdiProductKey: string;
  cfdiDescription: string;
  wholesaleCost?: number;
  priceMXN?: number;
}

export type ReleaseStage = 'disabled' | 'internal' | 'beta' | 'ga';

export interface FeatureRolloutState {
  stage: ReleaseStage;
  allowlist?: string[];
  percent?: number;
  owner?: string;
  since?: string;
}

export interface TenantPickerOption {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
}

export interface PlatformBillingResponse {
  gate: BillingGateState;
  catalog: Record<string, PlanDefinitionState>;
  addons: Record<string, AddonDefinitionState>;
  rollout: Record<string, FeatureRolloutState>;
  defaults: Record<string, PlanDefinitionState>;
  addonDefaults: Record<string, AddonDefinitionState>;
  rolloutDefaults: Record<string, FeatureRolloutState>;
  featureKeys: string[];
  featureLabels: Record<string, string>;
  limitKeys: string[];
  limitLabels: Record<string, string>;
  signupPolicies: Array<'pro_trial_then_free' | 'free_from_start'>;
  gatewayNames: Array<'stripe' | 'conekta' | 'openpay'>;
}

export interface StripePriceOptionState {
  id: string;
  unitAmount: number | null;
  currency: string;
  interval: 'month' | 'year' | string;
  productName: string;
  nickname: string | null;
  lookupKey: string | null;
}

export interface DecommissionReportState {
  scanned: number;
  tornDown: Array<{ tenantId: string; pagesProject: string | null; ok: boolean; detail?: string }>;
}

// ── Fiscal (CFDI / RESICO) ───────────────────────────────────────────────────
export type IvaMode = 'included' | 'added';
export type PacProvider = 'facturapi' | 'manual';

export interface FiscalConfigState {
  enabled: boolean;
  pacProvider: PacProvider;
  rfc: string;
  regimen: string;
  /** CP del domicilio fiscal del emisor. Es el `LugarExpedicion` del CFDI y el
   *  domicilio del receptor genérico en la factura global — sin él, el PAC
   *  rechaza el timbrado. */
  emisorZip: string;
  serie: string;
  ivaMode: IvaMode;
  ivaRate: number;
  currency: string;
  globalForUnrequested: boolean;
}

export interface PeriodSummaryState {
  period: string;
  count: number;
  amountTotal: number;
  refunded: number;
  iva: number;
  pending: number;
}

export interface PlatformFiscalResponse {
  config: FiscalConfigState;
  defaults: FiscalConfigState;
  periods: PeriodSummaryState[];
  closures: FiscalClosureState[];
  currentPeriod: string;
  ivaModes: IvaMode[];
  pacProviders: PacProvider[];
  manualStatuses: string[];
}

export interface FiscalRecordView {
  id: string;
  appUserId: string;
  kind: string;
  occurredAt: number;
  amountTotal: number;
  base: number;
  iva: number;
  currency: string;
  cfdiStatus: string;
  cfdiUuid: string | null;
  refundStatus: string | null;
  closureId: string | null;
  gatewayInvoiceId: string | null;
  gatewayChargeId: string | null;
  gatewayPaymentIntentId: string | null;
  note: string | null;
}

export interface FiscalTotalsState {
  count: number;
  amountTotal: number;
  base: number;
  iva: number;
}

export interface MonthlyReportState {
  period: string;
  currency: string;
  income: FiscalTotalsState;
  refunds: FiscalTotalsState;
  net: { amountTotal: number; base: number; iva: number };
  byStatus: Record<string, number>;
  pending: FiscalTotalsState;
  errorCount: number;
  records: FiscalRecordView[];
}

// ── Cierre mensual (GOL-10A) ────────────────────────────────────────────────
export type FiscalClosureStatus = 'draft' | 'issuing' | 'issued' | 'empty' | 'error';

export interface FiscalClosureState {
  period: string;
  status: FiscalClosureStatus;
  recordCount: number;
  amountTotal: number;
  base: number;
  iva: number;
  currency: string;
  recordIds: string[];
  cfdiUuid: string | null;
  cfdiProviderInvoiceId: string | null;
  cfdiPdfUrl: string | null;
  cfdiXmlUrl: string | null;
  note: string | null;
  issuedAt: number | null;
  updatedAt: number;
}

export interface FiscalClosurePreview {
  period: string;
  eligible: { count: number; amountTotal: number; base: number; iva: number; currency: string };
  recordIds: string[];
  excluded: { alreadyInvoiced: number; inClosure: number; errors: number; refunds: number };
  opensAt: number;
  windowOpen: boolean;
  emissionEnabled: boolean;
  emisorReady: boolean;
  closure: FiscalClosureState | null;
}

export type FiscalClosureRunResult =
  | { ok: true; closure: FiscalClosureState }
  | { ok: false; reason: string; message?: string; closure: FiscalClosureState | null };

// ── Legal (Términos/Aviso de Privacidad de la plataforma) ───────────────────
// El contrato es 1 por tenant, no por usuario: cualquier admin del tenant puede
// aceptarlo (los editores quedan cubiertos por adherencia, nunca aparecen aquí).
export interface LegalAcceptanceRow {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  adminEmails: string[];
  ownerEmail: string | null;
  acceptedByEmail: string | null;
  termsVersion: string | null;
  termsContentHash: string | null;
  privacyVersion: string | null;
  privacyContentHash: string | null;
  acceptedAt: number | null;
  pending: boolean;
}

export interface LegalDocumentVersionRow {
  id: number;
  version: string;
  content: string;
  contentHash: string;
  publishedAt: number;
}

// ── Centro de notificaciones (COMM-02) ───────────────────────────────────
export type NotificationSeverity = 'info' | 'warning' | 'action_required';

export interface NotificationItem {
  id: string;
  severity: NotificationSeverity;
  category: string;
  title: string;
  body: string | null;
  actionUrl: string | null;
  actionLabel: string | null;
  createdAt: number | null;
  readAt: number | null;
}

export type TenantAudienceMode = 'all' | 'plan' | 'onboarding' | 'tenant';

export interface BroadcastInput {
  target: 'tenant' | 'platform';
  title: string;
  body?: string;
  actionUrl?: string;
  actionLabel?: string;
  severity?: NotificationSeverity;
  audience?: { mode: TenantAudienceMode; value?: string };
  sendEmail?: boolean;
  emailRoles?: ('admin' | 'editor')[];
}

// Tenant activo para saas_admin. Se persiste en localStorage para sobrevivir recargas.
let _activeTenantId: string | null = (() => {
  try { return localStorage.getItem('pal_tenant'); } catch { return null; }
})();

// Capacidades del plan cacheadas a nivel de módulo (se invalidan al cambiar de tenant).
let _capsPromise: Promise<PanelCapabilities> | null = null;

export function clearActiveTenantId(): void {
  _activeTenantId = null;
  _capsPromise = null;
  try { localStorage.removeItem('pal_tenant'); } catch { /* ignore */ }
}

export function setActiveTenantId(id: string | null): void {
  _activeTenantId = id;
  _capsPromise = null;
  try {
    if (id) localStorage.setItem('pal_tenant', id);
    else localStorage.removeItem('pal_tenant');
  } catch { /* ignore */ }
}

export interface PanelCapabilities {
  plan: string;
  locales: string[];
  allowedLocales: string[];
  multilingual: boolean;
  aiImages: boolean;
  /** Ya cruzado con la etapa de madurez (FLG-01) — si la capacidad está activa. */
  features: Record<string, boolean>;
  /** Solo el eje comercial. Ojo: queda VACÍO cuando el acceso venció, así que no
   *  distingue "no existe" de "no pagado" — para eso está `stages`. */
  planFeatures: Record<string, boolean>;
  /** Etapa de cada feature. Es lo que decide candado vs esconder: si no es 'ga',
   *  la feature todavía no existe para clientes → esconder. */
  stages: Record<string, 'disabled' | 'internal' | 'beta' | 'ga'>;
  /** `false` con trial vencido o cancelado — el candado explica "tu acceso
   *  venció" en vez de "plan superior". */
  billingActive: boolean;
}

// Capacidades del plan, cacheadas una vez por tenant activo.
export function capabilitiesCached(): Promise<PanelCapabilities> {
  if (!_capsPromise) _capsPromise = api.settings.capabilities();
  return _capsPromise;
}

export function getActiveTenantId(): string | null {
  return _activeTenantId;
}

// Sub-toggle propio de Surveys (solo aplica a saas_admin): 'platform' = Destakia
// global, 'tenant' = el tenant activo del switcher general (`_activeTenantId`). No se
// persiste entre sesiones a propósito: cada carga nueva del panel arranca en 'platform'
// (regla de SURV-01 — el default al entrar a Surveys es Global).
let _surveysScope: 'platform' | 'tenant' = 'platform';

export function getSurveysScope(): 'platform' | 'tenant' {
  return _surveysScope;
}

export function setSurveysScope(scope: 'platform' | 'tenant'): void {
  _surveysScope = scope;
}

// `scopeOverride` fuerza un scope puntual para esta llamada sin tocar el estado global
// (usado por el fallback de SurveyEditorPage: reintentar con el otro scope no debe
// interferir con otra petición concurrente que esté leyendo `_surveysScope`).
function withTenantSuffix(path: string, scopeOverride?: 'platform' | 'tenant'): string {
  const params = new URLSearchParams();
  if (_activeTenantId) params.set('tenantId', _activeTenantId);
  const scope = scopeOverride ?? _surveysScope;
  if (path.startsWith('/surveys/') && scope === 'tenant') params.set('scope', 'tenant');
  const suffix = params.toString();
  return `${BASE}${path}${suffix ? (path.includes('?') ? '&' : '?') + suffix : ''}`;
}

/** Descarga un archivo servido por el panel (con la sesión del usuario) y lo
 *  guarda con su nombre. Se usa para comprobantes fiscales, que no pueden
 *  enlazarse directo al proveedor porque sus URLs exigen la clave del emisor. */
async function downloadFile(path: string, filename: string): Promise<void> {
  const res = await fetch(withTenantSuffix(path), { credentials: 'include' });
  if (!res.ok) {
    const detail = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(detail?.error ?? 'No pudimos descargar el archivo. Inténtalo de nuevo.');
  }
  const url = URL.createObjectURL(await res.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function req<T>(path: string, init?: RequestInit, scopeOverride?: 'platform' | 'tenant'): Promise<T> {
  const res = await fetch(withTenantSuffix(path, scopeOverride), {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    credentials: 'include',
  });
  if (res.status === 401) emitSessionExpired();
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    const { error, code, issues, requestId, retryable } = err as { error?: string; code?: string; issues?: unknown; requestId?: string; retryable?: boolean };
    // `code` es opcional — solo lo usan errores que el UI necesita distinguir de un
    // mensaje genérico (p. ej. SURV-14: activar sin material resuelve a `no_deliverable`
    // y ofrece confirmar en vez de solo mostrar el texto).
    throw Object.assign(new Error(error ?? res.statusText), {
      ...(code ? { code } : {}), ...(issues ? { issues } : {}),
      ...(requestId ? { requestId } : {}), ...(retryable !== undefined ? { retryable } : {}),
    });
  }
  return res.json();
}

async function authReq<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error: string }).error ?? res.statusText);
  }
  return res.json();
}

// Pages
export const api = {
  // Configuración global de IA (solo SaaS owner).
  admin: {
    getAiSettings: () => req<AiSettingsResponse>('/admin/ai'),
    saveAiSettings: (body: AiSettings) =>
      req<{ ok: boolean; settings: AiSettings }>('/admin/ai', { method: 'PUT', body: JSON.stringify(body) }),
    getSecuritySettings: () => req<SecuritySettingsResponse>('/admin/security'),
    saveSecuritySettings: (body: SecuritySettings) =>
      req<{ ok: boolean; settings: SecuritySettings }>('/admin/security', { method: 'PUT', body: JSON.stringify(body) }),
    getInfraSettings: () => req<InfraSettingsResponse>('/admin/infra'),
    saveInfraSettings: (body: InfraSettings) =>
      req<{ ok: boolean; settings: InfraSettings }>('/admin/infra', { method: 'PUT', body: JSON.stringify(body) }),
    getCredentials: () => req<PlatformCredentialsResponse>('/admin/credentials'),
    saveCredentials: (body: Partial<Record<PlatformCredentialField, string>>) =>
      req<PlatformCredentialsResponse>('/admin/credentials', { method: 'PUT', body: JSON.stringify(body) }),
    clearCredential: (field: PlatformCredentialField) =>
      req<PlatformCredentialsResponse>(`/admin/credentials/${field}`, { method: 'DELETE' }),
    getSupportSettings: () => req<SupportSettingsResponse>('/admin/support'),
    saveSupportSettings: (body: SupportSettings) =>
      req<{ ok: boolean; settings: SupportSettings; resolved: ResolvedSupportConfig }>(
        '/admin/support', { method: 'PUT', body: JSON.stringify(body) },
      ),
  },
  // Ayuda y soporte del panel (GOL-11): FAQ local + asistente + escalación.
  support: {
    config: () => req<SupportPublicConfig>('/support/config'),
    ask: (body: { question: string; history: SupportAskTurn[] }) =>
      req<SupportAskResult>('/support/ask', { method: 'POST', body: JSON.stringify(body) }),
    contact: (body: { message: string; path: string; askedBefore: string[] }) =>
      req<{ ok: boolean; promise: string; replyTo: string | null }>(
        '/support/contact', { method: 'POST', body: JSON.stringify(body) },
      ),
  },
  bookings: {
    list: (from?: string, to?: string) => {
      const qs = new URLSearchParams(); if (from) qs.set('from', from); if (to) qs.set('to', to);
      return req<BookingRecord[]>(`/bookings${qs.size ? `?${qs}` : ''}`);
    },
    reminderSettings: () => req<BookingReminderSettings>('/bookings/reminder-settings'),
    updateReminderSettings: (body: BookingReminderSettings) =>
      req<BookingReminderSettings>('/bookings/reminder-settings', { method: 'PUT', body: JSON.stringify(body) }),
    services: () => req<BookingService[]>('/bookings/services'),
    createService: (body: Partial<BookingService> & { name: string; durationMinutes: number }) => req<BookingService>('/bookings/services', { method: 'POST', body: JSON.stringify(body) }),
    updateService: (id: string, body: Partial<BookingService>) => req<BookingService>(`/bookings/services/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteService: (id: string) => req<{ ok: boolean }>(`/bookings/services/${id}`, { method: 'DELETE' }),
    groups: () => req<BookingStaffGroup[]>('/bookings/groups'),
    createGroup: (body: { name: string; description?: string; staffIds: string[] }) => req<BookingStaffGroup>('/bookings/groups', { method: 'POST', body: JSON.stringify(body) }),
    updateGroup: (id: string, body: Partial<BookingStaffGroup>) => req<BookingStaffGroup>(`/bookings/groups/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteGroup: (id: string) => req<{ ok: boolean }>(`/bookings/groups/${id}`, { method: 'DELETE' }),
    staff: () => req<BookingStaff[]>('/bookings/staff'),
    createStaff: (body: { name: string; email?: string; officeId?: string; collectionItemId?: string; timezone?: string; serviceIds: string[] }) => req<BookingStaff>('/bookings/staff', { method: 'POST', body: JSON.stringify(body) }),
    updateStaff: (id: string, body: { name: string; email?: string; officeId?: string; timezone?: string; serviceIds: string[] }) => req<BookingStaff>(`/bookings/staff/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteStaff: (id: string) => req<{ ok: boolean } | { error: string; blockingReason: 'active_bookings' | 'historical_bookings' }>(`/bookings/staff/${id}`, { method: 'DELETE' }),
    schedule: (staffId: string) => req<BookingSchedule>(`/bookings/staff/${staffId}/schedule`),
    saveSchedule: (staffId: string, rules: BookingSchedule['rules']) => req<{ ok: boolean }>(`/bookings/staff/${staffId}/schedule`, { method: 'PUT', body: JSON.stringify({ rules }) }),
    addTimeOff: (staffId: string, body: { startsAt: string; endsAt: string; reason?: string }) => req(`/bookings/staff/${staffId}/time-off`, { method: 'POST', body: JSON.stringify(body) }),
    deleteTimeOff: (staffId: string, id: string) => req<{ ok: boolean }>(`/bookings/staff/${staffId}/time-off/${id}`, { method: 'DELETE' }),
    setStatus: (id: string, status: BookingRecord['booking']['status']) => req<{ ok: boolean }>(`/bookings/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  },
  feedback: {
    summary: () => req<FeedbackSummary>('/feedback/summary'),
    settings: () => req<FeedbackSettingsRecord>('/feedback/settings'),
    updateSettings: (body: FeedbackSettingsRecord) => req<FeedbackSettingsRecord>('/feedback/settings', { method: 'PUT', body: JSON.stringify(body) }),
    requests: () => req<FeedbackRequestRecord[]>('/feedback/requests'),
    createRequest: (body: {
      bookingId?: string;
      customerName?: string;
      customerEmail?: string;
      customerPhone?: string;
      channel?: 'link' | 'email' | 'whatsapp';
      scoreType?: 'csat' | 'nps';
    }) => req<FeedbackRequestRecord>('/feedback/requests', { method: 'POST', body: JSON.stringify(body) }),
    sendRequest: (id: string) => req<{ ok: boolean; request: FeedbackRequestRecord | null; sharePath: string | null; shareUrl: string | null }>(`/feedback/requests/${id}/send`, { method: 'POST' }),
    responses: () => req<FeedbackResponseRecord[]>('/feedback/responses'),
    reviewResponse: (id: string, status: 'reviewed' | 'archived') => req<{ ok: boolean; status: string }>(`/feedback/responses/${id}/review`, { method: 'POST', body: JSON.stringify({ status }) }),
  },
  surveys: {
    listCampaigns: () => req<SurveyCampaignListItem[]>('/surveys/campaigns'),
    createCampaign: (body: { title?: string; campaignPurpose?: SurveyCampaignPurpose; objective?: SurveyObjective; hostMode?: SurveyHostMode; cta?: { label?: string; url?: string } }) =>
      req<SurveyCampaignListItem>('/surveys/campaigns', { method: 'POST', body: JSON.stringify(body) }),
    getCampaign: (id: string, scopeOverride?: 'platform' | 'tenant') =>
      req<SurveyCampaignDetail>(`/surveys/campaigns/${id}`, undefined, scopeOverride),
    updateCampaign: (id: string, body: {
      title?: string; slug?: string; campaignPurpose?: SurveyCampaignPurpose; objective?: SurveyObjective; status?: SurveyStatus; hostMode?: SurveyHostMode; settings?: SurveyCampaignSettings;
      confirmNoDeliverable?: boolean;
    }) => req<unknown>(`/surveys/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteCampaign: (id: string) => req<{ ok: boolean }>(`/surveys/campaigns/${id}`, { method: 'DELETE' }),
    createVariant: (campaignId: string, body: { audience: string; title?: string; intro?: string }) =>
      req<SurveyVariantRecord>(`/surveys/campaigns/${campaignId}/variants`, { method: 'POST', body: JSON.stringify(body) }),
    updateVariant: (id: string, body: { title?: string; intro?: string; deliverableId?: string | null; settings?: { cta?: { label?: string; url?: string }; thankYou?: { title?: string; body?: string } } }) =>
      req<SurveyVariantRecord>(`/surveys/variants/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteVariant: (id: string) => req<{ ok: boolean }>(`/surveys/variants/${id}`, { method: 'DELETE' }),
    createQuestion: (campaignId: string, body: {
      variantId?: string | null; key?: string; type?: SurveyQuestionType; label?: string;
      helpText?: string; placeholder?: string; options?: SurveyOption[]; rules?: Record<string, unknown>; required?: boolean; stepOrder?: number;
    }) => req<SurveyQuestionRecord>(`/surveys/campaigns/${campaignId}/questions`, { method: 'POST', body: JSON.stringify(body) }),
    updateQuestion: (id: string, body: {
      label?: string; helpText?: string; placeholder?: string; type?: SurveyQuestionType;
      options?: SurveyOption[]; rules?: Record<string, unknown>; required?: boolean; stepOrder?: number; variantId?: string | null;
    }) => req<SurveyQuestionRecord>(`/surveys/questions/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteQuestion: (id: string) => req<{ ok: boolean }>(`/surveys/questions/${id}`, { method: 'DELETE' }),
    // Carga atómica de cascada (SURV-02): escribe las opciones del padre y el cascadeMap
    // de la hija en una sola transacción — nunca dos PUT sueltos desde el cliente.
    cascadeImport: (campaignId: string, body: {
      parentQuestionId: string; childQuestionId: string; parentOptions: SurveyOption[]; cascadeMap: CascadeOptionsMap;
    }) => req<{ parent: SurveyQuestionRecord; child: SurveyQuestionRecord; groups: number; options: number }>(
      `/surveys/campaigns/${campaignId}/cascade-import`, { method: 'PUT', body: JSON.stringify(body) },
    ),
    responses: (campaignId: string, filters?: { audience?: string; status?: SurveyLeadStatus }) => {
      const qs = new URLSearchParams();
      if (filters?.audience) qs.set('audience', filters.audience);
      if (filters?.status) qs.set('status', filters.status);
      return req<SurveyResponseRecord[]>(`/surveys/campaigns/${campaignId}/responses${qs.size ? `?${qs}` : ''}`);
    },
    updateResponse: (id: string, body: { status?: SurveyLeadStatus; markRead?: boolean }) =>
      req<unknown>(`/surveys/responses/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteResponse: (id: string) => req<{ ok: boolean }>(`/surveys/responses/${id}`, { method: 'DELETE' }),
    listDeliverables: () => req<SurveyDeliverableRecord[]>('/surveys/deliverables'),
    createDeliverable: (body: { title?: string; mediaId?: number; emailSubject?: string; versionLabel?: string }) =>
      req<SurveyDeliverableRecord>('/surveys/deliverables', { method: 'POST', body: JSON.stringify(body) }),
    updateDeliverable: (id: string, body: { title?: string; mediaId?: number | null; emailSubject?: string; versionLabel?: string }) =>
      req<SurveyDeliverableRecord>(`/surveys/deliverables/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteDeliverable: (id: string) => req<{ ok: boolean }>(`/surveys/deliverables/${id}`, { method: 'DELETE' }),
  },
  testimonials: {
    list: () => req<TestimonialRecord[]>('/testimonials'),
    create: (body: {
      feedbackResponseId?: string;
      quote?: string;
      authorName?: string;
      authorRole?: string;
      authorLocation?: string;
      ratingValue?: number | null;
    }) => req<TestimonialRecord>('/testimonials', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: {
      quote: string;
      authorName: string;
      authorRole?: string;
      authorLocation?: string;
      ratingValue?: number | null;
    }) => req<TestimonialRecord>(`/testimonials/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    approve: (id: string) => req<TestimonialRecord>(`/testimonials/${id}/approve`, { method: 'POST' }),
    reject: (id: string) => req<TestimonialRecord>(`/testimonials/${id}/reject`, { method: 'POST' }),
    feature: (id: string, featured: boolean) => req<TestimonialRecord>(`/testimonials/${id}/feature`, { method: 'POST', body: JSON.stringify({ featured }) }),
  },
  legal: {
    get: () => req<LegalDoc[]>('/legal'),
    fromTemplate: (docKey: LegalDocKey, locale: 'es' | 'en') =>
      req<{ title: string; body: string }>(`/legal/${docKey}/from-template`, { method: 'POST', body: JSON.stringify({ locale }) }),
    save: (docKey: LegalDocKey, body: { locale: 'es' | 'en'; title?: string; body: string; publish?: boolean }) =>
      req<LegalDocLocale>(`/legal/${docKey}`, { method: 'PUT', body: JSON.stringify(body) }),
    visitorRequests: {
      list: () => req<{ requests: VisitorRequest[] }>('/legal/visitor-requests'),
      decide: (id: string, body: { status: Extract<VisitorRequestStatus, 'in_review' | 'resolved' | 'denied'>; decision?: string }) =>
        req<VisitorRequest>(`/legal/visitor-requests/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      purge: (id: string) =>
        req<{ ok: boolean; redacted: Record<string, number> }>(`/legal/visitor-requests/${id}/purge`, { method: 'POST' }),
    },
  },
  links: {
    get: () => req<LinksResponse>('/links'),
    save: (body: { locale: 'es' | 'en'; props: BioProps; publish?: boolean }) =>
      req<LinksLocaleState>('/links', { method: 'PUT', body: JSON.stringify(body) }),
  },
  announcements: {
    list: () => req<AnnouncementRecord[]>('/announcements'),
    create: (body: AnnouncementInput) =>
      req<AnnouncementRecord>('/announcements', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: AnnouncementInput) =>
      req<AnnouncementRecord>(`/announcements/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    remove: (id: string) =>
      req<{ ok: boolean }>(`/announcements/${id}`, { method: 'DELETE' }),
  },

  pages: {
    list: () => req<Page[]>('/pages'),
    get: (id: string) => req<Page>(`/pages/${id}`),
    getDraft: (id: string) => req<{ pageId: string; tenantId: string; contentJson: string; updatedAt: Date | null }>(`/pages/${id}/draft`),
    create: (body: { slug: string; locale: string; title: string; pageKey?: string; campaignChannel?: string; campaignLabel?: string }) =>
      req<Page>('/pages', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<Page>) =>
      req<Page>(`/pages/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    saveDraft: (id: string, contentJson: string) =>
      req<{ pageId: string; tenantId: string; contentJson: string; updatedAt: Date | null }>(`/pages/${id}/draft`, { method: 'PUT', body: JSON.stringify({ contentJson }) }),
    reconcileDraft: (id: string) =>
      req<{ pageId: string; tenantId: string; contentJson: string; updatedAt: Date | null }>(`/pages/${id}/draft/reconcile`, { method: 'POST' }),
    saveContent: (id: string, contentJson: string) =>
      req<Page>(`/pages/${id}/content`, { method: 'PUT', body: JSON.stringify({ contentJson }) }),
    versions: (id: string) => req<PageVersion[]>(`/pages/${id}/versions`),
    // SRV-18 — páginas listas para captar una intención concreta (vender una propiedad,
    // pedir una cotización), con su versión en español y en inglés.
    leadIntents: () => req<LeadIntentPage[]>('/pages/lead-intents'),
    setLeadIntent: (intent: string, enabled: boolean) =>
      req<{ ok: boolean; enabled: boolean; pageKey: string }>(`/pages/lead-intents/${intent}`, {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
    delete: (id: string, opts?: { force?: boolean }) => fetch(withTenantSuffix(`/pages/${id}${opts?.force ? '?force=1' : ''}`), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    }).then(async (res) => {
      if (res.ok) return res.json() as Promise<{ ok: boolean }>;
      const err = await res.json().catch(() => ({ error: res.statusText }));
      if ('blockingReason' in (err as Record<string, unknown>)) return err as PageDeleteBlockedResponse;
      throw new Error((err as { error?: string }).error ?? res.statusText);
    }),
    // Returns the sibling locale page (same page_key, different locale), or null
    getSibling: async (page: import('./types').Page): Promise<import('./types').Page | null> => {
      if (!page.pageKey) return null;
      const all = await req<import('./types').Page[]>('/pages');
      return all.find((p) => p.pageKey === page.pageKey && p.locale !== page.locale) ?? null;
    },
  },

  // Motor de colecciones genéricas — reemplaza a properties/agents/posts.
  collectionTypes: {
    list: () => req<CollectionTypeDef[]>('/collection-types'),
    get: (key: string) => req<CollectionTypeDef>(`/collection-types/${key}`),
    updateLabels: (key: string, labels: { singular?: { es?: string; en?: string }; plural?: { es?: string; en?: string } }) =>
      req<{ ok: boolean; labels: { singular: Record<string, string>; plural: Record<string, string> } }>(
        `/collection-types/${key}/labels`, { method: 'PATCH', body: JSON.stringify(labels) }),
  },

  collections: {
    // En el panel listamos todo (draft + published); el sitio filtra published.
    list: (key: string, opts?: { status?: 'draft' | 'published'; featured?: '0' | '1'; locale?: string }) => {
      const qs = new URLSearchParams();
      if (opts?.status) qs.set('status', opts.status);
      if (opts?.featured) qs.set('featured', opts.featured);
      if (opts?.locale) qs.set('locale', opts.locale);
      const suffix = qs.toString() ? `?${qs}` : '';
      return req<CollectionItem[]>(`/collections/${key}/items${suffix}`);
    },
    get: (key: string, id: string) => req<CollectionItem>(`/collections/${key}/items/${id}`),
    create: (key: string, body: { data: Record<string, unknown>; status?: 'draft' | 'published'; featured?: boolean; sortOrder?: number; confirmLocalizedFields?: string[] }) =>
      req<CollectionItem>(`/collections/${key}/items`, { method: 'POST', body: JSON.stringify(body) }),
    // `confirmLocalizedFields` — GEO-48: campos que el editor marcó "revisado" en esta sesión,
    // aunque el texto en inglés no haya cambiado. El worker sincroniza su huella igual que si
    // el inglés se hubiera editado.
    update: (key: string, id: string, body: { data?: Record<string, unknown>; status?: 'draft' | 'published'; featured?: boolean; sortOrder?: number; confirmLocalizedFields?: string[] }) =>
      req<CollectionItem>(`/collections/${key}/items/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    reorder: (key: string, ids: string[]) =>
      req<{ items: CollectionItem[] }>(`/collections/${key}/items/reorder`, { method: 'PUT', body: JSON.stringify({ ids }) }),
    delete: (key: string, id: string) =>
      req<{ ok: boolean }>(`/collections/${key}/items/${id}`, { method: 'DELETE' }),
  },

  marketingAssets: {
    list: (opts?: { collectionKey?: string; kind?: 'pdf_flyer' | 'pdf_catalog'; status?: 'pending' | 'ready' | 'error' }) => {
      const qs = new URLSearchParams();
      if (opts?.collectionKey) qs.set('collectionKey', opts.collectionKey);
      if (opts?.kind) qs.set('kind', opts.kind);
      if (opts?.status) qs.set('status', opts.status);
      return req<MarketingAssetRecord[]>(`/marketing-assets${qs.size ? `?${qs}` : ''}`);
    },
    get: (id: string) => req<MarketingAssetRecord>(`/marketing-assets/${id}`),
    createFlyer: (body: { collectionKey: string; itemId: string; locale: 'es' | 'en'; templateKey?: 'single-item-flyer' }) =>
      req<MarketingAssetRecord>('/marketing-assets/flyer', { method: 'POST', body: JSON.stringify(body) }),
    createImage: (body: { collectionKey: string; itemId: string; locale: 'es' | 'en'; format: 'feed' | 'story' }) =>
      req<MarketingAssetRecord>('/marketing-assets/image', { method: 'POST', body: JSON.stringify(body) }),
    createCatalog: (body: { collectionKey: string; itemIds: string[]; locale: 'es' | 'en'; title?: string; subtitle?: string; templateKey?: 'catalog-grid' | 'catalog-pages' }) =>
      req<MarketingAssetRecord>('/marketing-assets/catalog', { method: 'POST', body: JSON.stringify(body) }),
    regenerate: (id: string) =>
      req<MarketingAssetRecord>(`/marketing-assets/${id}/regenerate`, { method: 'POST' }),
    remove: (id: string) =>
      req<{ ok: boolean }>(`/marketing-assets/${id}`, { method: 'DELETE' }),
  },

  media: {
    // `scope: 'platform'` lista/sube/borra material namespaceado bajo el tenant de marca
    // de la plataforma (resuelto server-side), no el tenant activo del sidebar — solo
    // saas_admin puede pedirlo (ver `resolveMediaTenantId` en apps/panel/worker/routes/media.ts).
    // Nació para el material de encuestas globales (SURV-20) y se reusa en `/media` (MED-01).
    list: (opts?: { scope?: 'platform' }) =>
      req<MediaItem[]>(opts?.scope === 'platform' ? '/media?scope=platform' : '/media'),
    library: (opts?: { scope?: 'platform' }) =>
      req<MediaLibraryItem[]>(opts?.scope === 'platform' ? '/media/library?scope=platform' : '/media/library'),
    upload: (
      file: File,
      folder?: string,
      context?: MediaUploadContext,
      scope?: 'platform',
      uploadToken?: string,
    ) => {
      const form = new FormData();
      form.append('file', file);
      form.append('uploadToken', uploadToken ?? crypto.randomUUID());
      if (folder) form.append('folder', folder);
      if (context?.pageId) form.append('pageId', context.pageId);
      if (context?.blockType) form.append('blockType', context.blockType);
      if (context?.fieldKey) form.append('fieldKey', context.fieldKey);
      if (context?.collectionKey) form.append('collectionKey', context.collectionKey);
      if (context?.itemId) form.append('itemId', context.itemId);
      // Material de una encuesta global namespacea implícitamente en platform; el resto
      // de la UI (p. ej. MediaPage) lo pide explícitamente vía el 4º parámetro.
      const isPlatformScope = scope === 'platform'
        || (context?.blockType === 'survey-deliverable' && getSurveysScope() === 'platform');
      const url = isPlatformScope ? `${BASE}/media/upload?scope=platform` : withTenantSuffix('/media/upload');
      return fetch(url, { method: 'POST', body: form, credentials: 'include' }).then(async (r) => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({ error: r.statusText }));
          const detail = err as { error?: string; code?: string; requestId?: string; retryable?: boolean };
          throw Object.assign(new Error(detail.error ?? 'No se pudo subir el archivo.'), {
            code: detail.code, requestId: detail.requestId, retryable: detail.retryable,
          });
        }
        return r.json() as Promise<MediaItem>;
      });
    },
    delete: (id: number, scope?: 'platform') =>
      req<{ ok: boolean }>(scope === 'platform' ? `/media/${id}?scope=platform` : `/media/${id}`, { method: 'DELETE' }),
  },

  // Códigos QR — reutilizable por cualquier módulo. Un QR canónico por (tenant, targetUrl).
  qr: {
    list: (opts?: { module?: string; refId?: string }) => {
      const qs = new URLSearchParams();
      if (opts?.module) qs.set('module', opts.module);
      if (opts?.refId) qs.set('refId', opts.refId);
      return req<QrCodeRecord[]>(`/qr${qs.size ? `?${qs}` : ''}`);
    },
    save: (body: { file: Blob; module: string; refId?: string; label: string; targetUrl: string; styleJson: string }) => {
      const form = new FormData();
      form.append('file', body.file, 'qr.png');
      form.append('module', body.module);
      if (body.refId) form.append('refId', body.refId);
      form.append('label', body.label);
      form.append('targetUrl', body.targetUrl);
      form.append('styleJson', body.styleJson);
      return fetch(`${BASE}/qr`, { method: 'POST', body: form, credentials: 'include' }).then((r) => {
        if (!r.ok) return r.json().then((e) => { throw new Error((e as { error?: string }).error ?? r.statusText); });
        return r.json() as Promise<QrCodeRecord>;
      });
    },
    remove: (id: string) => req<{ ok: boolean }>(`/qr/${id}`, { method: 'DELETE' }),
  },

  settings: {
    get: () => req<SiteSettings | null>('/settings'),
    update: (body: Partial<SiteSettings>) =>
      req<SiteSettings>('/settings', { method: 'PUT', body: JSON.stringify(body) }),
    // SRV-03 — preguntas del formulario de contacto.
    getBrief: () => req<BriefSettingsResponse>('/settings/brief'),
    saveBrief: (body: BriefConfig) =>
      req<BriefSettingsResponse>('/settings/brief', { method: 'PUT', body: JSON.stringify(body) }),
    getTheme: () => req<BrandingTheme>('/settings/theme'),
    updateTheme: (body: BrandingTheme) =>
      req<{ ok: boolean; theme: BrandingTheme }>('/settings/theme', { method: 'PUT', body: JSON.stringify(body) }),
    // DSN-21 — un nivel de deshacer sobre el tema guardado.
    hasPreviousTheme: () => req<{ available: boolean }>('/settings/theme/previous'),
    undoTheme: () => req<{ theme: BrandingTheme }>('/settings/theme/undo', { method: 'POST' }),
    listUsers: () => req<User[]>('/settings/users'),
    createUser: (body: { email: string; role: 'admin' | 'editor'; delivery?: 'email' | 'none'; firstName: string; lastName: string; phone: string }) =>
      req<User>('/settings/users', { method: 'POST', body: JSON.stringify(body) }),
    updateUser: (id: number, body: { email?: string; role?: 'admin' | 'editor' }) =>
      req<User>(`/settings/users/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    resendUserAccess: (id: number, delivery: 'email' | 'manual') => req<{ ok: true; devLink?: string; accessUrl?: string }>(`/settings/users/${id}/access`, { method: 'POST', body: JSON.stringify({ delivery }) }),
    deleteUser: (id: number) => req<{ ok: boolean }>(`/settings/users/${id}`, { method: 'DELETE' }),
    getSite: () => req<{
      name: string;
      slug: string;
      domain: string | null;
      bookingTimezone: string;
      bookingsEnabled: boolean;
    }>('/settings/site'),
    updateSite: (body: {
      name?: string;
      bookingTimezone?: string;
      bookingsEnabled?: boolean;
    }) =>
      req('/settings/site', { method: 'PUT', body: JSON.stringify(body) }),
    getPublishingInfra: (tenantId?: string) => req<{
      deployHookUrl: string | null;
      pagesProject: string | null;
      deployAccountId: string | null;
      deployTokenSet: boolean;
    } | null>(`/settings/site/publishing-infra${tenantId ? `?tenantId=${tenantId}` : ''}`),
    updatePublishingInfra: (body: {
      deployHookUrl?: string;
      pagesProject?: string;
      deployAccountId?: string;
      deployToken?: string;
    }, tenantId?: string) =>
      req(`/settings/site/publishing-infra${tenantId ? `?tenantId=${tenantId}` : ''}`, { method: 'PUT', body: JSON.stringify(body) }),
    capabilities: () => req<PanelCapabilities>('/settings/capabilities'),
    updateLocales: (locales: string[]) =>
      req<{ locales: string[]; multilingual: boolean }>('/settings/locales', {
        method: 'PUT',
        body: JSON.stringify({ locales }),
      }),
  },

  domains: {
    status: () => req<CloudflareDomainStatus>('/domains/status'),
    updateConfig: (body: {
      accountId?: string;
      apiToken?: string;
      primaryDomain?: string;
      transferState?: Record<string, TutorialProgressState>;
    }) => req<{ ok: boolean; primaryDomain: string | null; accountId: string | null; apiTokenConfigured: boolean }>('/domains/config', { method: 'PUT', body: JSON.stringify(body) }),
    search: (query: string) => req<{ query: string; results: DomainSearchResult[] }>('/domains/search', { method: 'POST', body: JSON.stringify({ query }) }),
    check: (domain: string) => req<DomainCheckResult>('/domains/check', { method: 'POST', body: JSON.stringify({ domain }) }),
    register: (domain: string) => req<{ domain: string; workflowState: string; completed: boolean; statusUrl: string | null; resourceUrl: string | null; message: string }>('/domains/register', { method: 'POST', body: JSON.stringify({ domain }) }),
    connect: (domain: string) => req<DomainConnectResult>('/domains/connect', { method: 'POST', body: JSON.stringify({ domain }) }),
    refresh: () => req<{ domain: string; pagesDomain: DomainConnectResult['pagesDomain']; message: string }>('/domains/refresh', { method: 'POST' }),
    updateRedirects: (body: RedirectPresetState) => req<RedirectPresetState>('/domains/redirects', { method: 'PUT', body: JSON.stringify(body) }),
    transferGuide: () => req<{ steps: Array<{ id: string; title: string; detail: string }> }>('/domains/transfer-guide'),
  },

  // Google Business — Nivel 1 (Places API): vincular la ficha y leer rating/reseñas.
  google: {
    get: () => req<GoogleBusinessRecord | null>('/google'),
    search: (q: string) =>
      req<{ results: GooglePlaceCandidate[] }>(`/google/search?q=${encodeURIComponent(q)}`),
    link: (placeId: string) =>
      req<GoogleBusinessRecord>('/google', { method: 'PUT', body: JSON.stringify({ placeId }) }),
    refresh: () => req<GoogleBusinessRecord>('/google/refresh', { method: 'POST' }),
    applyProfile: (fields: Array<'businessName' | 'phone' | 'address' | 'hours'>) =>
      req<{ applied: string[]; cachedAt: number | null }>('/google/apply-profile', { method: 'POST', body: JSON.stringify({ fields }) }),
    setVisibility: (showOnSite: boolean) =>
      req<GoogleBusinessRecord>('/google/visibility', { method: 'PUT', body: JSON.stringify({ showOnSite }) }),
    unlink: () => req<{ ok: boolean }>('/google', { method: 'DELETE' }),
  },

  publish: {
    trigger: () => req<{ logId: number; status: string }>('/publish', { method: 'POST' }),
    log: () => req<PublishLog[]>('/publish/log'),
    publishPage: (pageId: string) => req<Page>(`/publish/page/${pageId}`, { method: 'POST' }),
  },

  compliance: {
    warnings: () => req<{ warnings: ComplianceWarning[] }>('/compliance/warnings'),
  },

  submissions: {
    list: () => req<FormSubmission[]>('/submissions'),
    stats: () => req<SubmissionStats>('/submissions/stats'),
    unreadCount: () => req<{ count: number }>('/submissions/unread-count'),
    markRead: () => req<{ ok: boolean }>('/submissions/mark-read', { method: 'POST' }),
    markOneRead: (id: number) => req<{ ok: boolean }>(`/submissions/${id}/read`, { method: 'PATCH' }),
    setAttended: (id: number, attended: boolean) =>
      req<{ ok: boolean; attendedAt: Date | null }>(`/submissions/${id}/attended`, {
        method: 'PATCH',
        body: JSON.stringify({ attended }),
      }),
    remove: (id: number) => req<{ ok: boolean }>(`/submissions/${id}`, { method: 'DELETE' }),
    // COMM-09 — reintenta el aviso de un mensaje ya guardado. Idempotente en el
    // worker: si el correo sí había salido, no manda un segundo.
    retryNotice: (id: number) =>
      req<{ ok: boolean; outcome: string }>(`/submissions/${id}/notice/retry`, { method: 'POST' }),
  },

  contacts: {
    list: (params?: { search?: string; status?: string; source?: string; assignedTo?: string; jobStatus?: string; hasOpenJob?: boolean; attention?: boolean }) => {
      const query = new URLSearchParams();
      for (const [key, value] of Object.entries(params ?? {})) if (value) query.set(key, String(value));
      return req<ContactListResponse>(`/contacts${query.toString() ? `?${query}` : ''}`);
    },
    get: (id: string) => req<ContactDetailResponse>(`/contacts/${id}`),
    // CLI-24 — registrar una persona a mano desde la guía de "Nueva operación".
    create: (body: { name?: string | null; email?: string | null; phone?: string | null }) =>
      req<ContactRecord>('/contacts', { method: 'POST', body: JSON.stringify(body) }),
    duplicates: (id: string) => req<ContactDuplicateCandidate[]>(`/contacts/${id}/duplicates`),
    update: (id: string, body: { name?: string | null; email?: string | null; phone?: string | null; status?: string; assignedToUserId?: number | null; nextStep?: string | null; nextStepAt?: string | null }) =>
      req<ContactRecord>(`/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    bulkUpdate: (body: { ids: string[]; status?: string; assignedToUserId?: number | null }) =>
      req<{ contacts: ContactRecord[] }>(`/contacts/bulk`, { method: 'PATCH', body: JSON.stringify(body) }),
    merge: (id: string, sourceId: string) =>
      req<ContactRecord>(`/contacts/${id}/merge`, { method: 'POST', body: JSON.stringify({ sourceId }) }),
    importPreview: (body: { importKey: string; rows: ContactImportRow[] }) =>
      req<ContactImportPreviewResponse>('/contacts/import/preview', { method: 'POST', body: JSON.stringify(body) }),
    // CLI-04 — declaredOrigin es obligatorio: el import no confirma sin decir de dónde vienen los contactos.
    import: (body: { importKey: string; rows: ContactImportRow[]; declaredOrigin: string }) =>
      req<ContactImportResultResponse>('/contacts/import', { method: 'POST', body: JSON.stringify(body) }),
    backfill: () => req<{ contacts: number; interactions: number }>('/contacts/backfill', { method: 'POST' }),
    // CLI-04 — acceso y cancelación a petición de la persona.
    export: (id: string) => req<unknown>(`/contacts/${id}/export`),
    erase: (id: string) => req<{ ok: true }>(`/contacts/${id}`, { method: 'DELETE' }),
    // HIP-01 — estados de la libreta definidos por el dueño, no fijos en el código.
    statuses: {
      list: () => req<{ statuses: ContactStatusRecord[] }>('/contacts/statuses'),
      create: (label: string) => req<ContactStatusRecord>('/contacts/statuses', { method: 'POST', body: JSON.stringify({ label }) }),
      rename: (id: string, label: string) => req<ContactStatusRecord>(`/contacts/statuses/${id}`, { method: 'PATCH', body: JSON.stringify({ label }) }),
      reorder: (ids: string[]) => req<{ statuses: ContactStatusRecord[] }>('/contacts/statuses/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),
      remove: (id: string, reassignTo?: string) =>
        req<{ statuses: ContactStatusRecord[] }>(`/contacts/statuses/${id}${reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : ''}`, { method: 'DELETE' }),
    },
    // CLI-08 — el trabajo en curso como cosa propia, no como estado del cliente.
    jobs: {
      create: (contactId: string, body: { title?: string | null; status?: string; responsibleUserId?: number | null; serviceId?: string | null; templateId?: string | null; startStageId?: string | null }) =>
        req<ContactJobMutation>(`/contacts/${contactId}/jobs`, { method: 'POST', body: JSON.stringify(body) }),
      // CLI-22 — mover de etapa es su propia acción: aplica la regla de pendientes,
      // puede pedir confirmación (409) y siempre deja historial.
      moveStage: (contactId: string, jobId: string, body: { stageId: string; reason?: string | null; confirmed?: boolean }) =>
        req<ContactJobMutation | { needsConfirmation: true; unresolvedLabels: string[] }>(`/contacts/${contactId}/jobs/${jobId}/stage`, { method: 'POST', body: JSON.stringify(body) }),
      reopen: (contactId: string, jobId: string, body: { stageId?: string | null; reason: string }) =>
        req<ContactJobMutation>(`/contacts/${contactId}/jobs/${jobId}/reopen`, { method: 'POST', body: JSON.stringify(body) }),
      // CLI-28 — reparación guiada del carril "Sin paso a paso": adjunta un proceso a
      // una operación que nació sin ninguno, sin tocar los pendientes que ya tenía.
      attachStages: (contactId: string, jobId: string, body: { templateId: string; startStageId?: string | null }) =>
        req<ContactJobMutation>(`/contacts/${contactId}/jobs/${jobId}/stages/attach`, { method: 'POST', body: JSON.stringify(body) }),
      history: (contactId: string, jobId: string) =>
        req<{ events: JobStageEventRecord[] }>(`/contacts/${contactId}/jobs/${jobId}/history`),
      update: (contactId: string, jobId: string, body: { title?: string | null; status?: string; responsibleUserId?: number | null; closed?: boolean; serviceId?: string | null; digestEnabled?: boolean }) =>
        req<ContactJobMutation>(`/contacts/${contactId}/jobs/${jobId}`, { method: 'PATCH', body: JSON.stringify(body) }),
      // HIP-02 — qué le falta a cada cliente, sin guardar sus documentos.
      pendingItems: {
        create: (contactId: string, jobId: string, body: { label: string; dueAt?: string | null }) =>
          req<PendingItemRecord>(`/contacts/${contactId}/jobs/${jobId}/pending-items`, { method: 'POST', body: JSON.stringify(body) }),
        update: (contactId: string, jobId: string, itemId: string, body: { label?: string; status?: PendingItemRecord['status']; dueAt?: string | null }) =>
          req<PendingItemRecord>(`/contacts/${contactId}/jobs/${jobId}/pending-items/${itemId}`, { method: 'PATCH', body: JSON.stringify(body) }),
        remove: (contactId: string, jobId: string, itemId: string) =>
          req<{ ok: true }>(`/contacts/${contactId}/jobs/${jobId}/pending-items/${itemId}`, { method: 'DELETE' }),
        // CLI-16 — precarga los pendientes de una plantilla al crear el trabajo.
        applyTemplate: (contactId: string, jobId: string, body: { templateId: string; role?: PendingItemTemplateRole | null; anchorDate?: string | null }) =>
          req<{ items: PendingItemRecord[] }>(`/contacts/${contactId}/jobs/${jobId}/pending-items/apply-template`, { method: 'POST', body: JSON.stringify(body) }),
      },
      // CLI-21 — invitación de reseña por este trabajo. Idempotente: pedirla dos veces
      // devuelve el mismo enlace en vez de crear otra.
      requestReview: (contactId: string, jobId: string) =>
        req<{ url: string; reused: boolean; customerEmail: string | null; expiresAt: string | null }>(
          `/contacts/${contactId}/jobs/${jobId}/review-request`, { method: 'POST' },
        ),
      // SRV-07 — enlace público de seguimiento; generarlo de nuevo rota (invalida) el anterior.
      trackerLink: (contactId: string, jobId: string) =>
        req<{ url: string; expiresAt: string }>(`/contacts/${contactId}/jobs/${jobId}/tracker-link`, { method: 'POST' }),
    },
    // CLI-06 — aviso de contacto sin movimiento.
    followup: () => req<ContactFollowupResponse>('/contacts/followup'),
    // CLI-17 — frecuencia del correo de pendientes al cliente (por tenant).
    pendingItemDigestSettings: () => req<PendingItemDigestSettings>('/contacts/pending-item-digest-settings'),
    updatePendingItemDigestSettings: (body: { enabled: boolean; frequency: PendingItemDigestSettings['frequency'] }) =>
      req<PendingItemDigestSettings>('/contacts/pending-item-digest-settings', { method: 'PUT', body: JSON.stringify(body) }),
    followupSettings: () => req<ContactFollowupSettings>('/contacts/followup-settings'),
    updateFollowupSettings: (body: { enabled: boolean; thresholdDays: number; frequency: 'daily' | 'weekly' }) =>
      req<ContactFollowupSettings>('/contacts/followup-settings', { method: 'PUT', body: JSON.stringify(body) }),
    // CLI-16 — motor de plantillas de pendientes por industria.
    pendingItemTemplates: {
      list: () => req<{ templates: PendingItemTemplateRecord[] }>('/contacts/pending-item-templates'),
      create: (body: { name: string; roleALabel?: string | null; roleBLabel?: string | null; serviceId?: string | null }) =>
        req<PendingItemTemplateRecord>('/contacts/pending-item-templates', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: { name?: string; roleALabel?: string | null; roleBLabel?: string | null; serviceId?: string | null; blockOnPending?: boolean }) =>
        req<PendingItemTemplateRecord>(`/contacts/pending-item-templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      duplicate: (id: string) => req<PendingItemTemplateRecord>(`/contacts/pending-item-templates/${id}/duplicate`, { method: 'POST' }),
      remove: (id: string) => req<{ ok: true }>(`/contacts/pending-item-templates/${id}`, { method: 'DELETE' }),
      // CLI-22 — las etapas del paso a paso.
      stages: {
        create: (templateId: string, body: { name: string; category?: StageCategory; responsibleParty?: ResponsibleParty; externalDependencyLabel?: string | null; attentionThresholdDays?: number | null }) =>
          req<TemplateStageRecord>(`/contacts/pending-item-templates/${templateId}/stages`, { method: 'POST', body: JSON.stringify(body) }),
        update: (templateId: string, stageId: string, body: { name?: string; category?: StageCategory; responsibleParty?: ResponsibleParty; externalDependencyLabel?: string | null; attentionThresholdDays?: number | null }) =>
          req<TemplateStageRecord>(`/contacts/pending-item-templates/${templateId}/stages/${stageId}`, { method: 'PATCH', body: JSON.stringify(body) }),
        remove: (templateId: string, stageId: string) =>
          req<{ ok: true }>(`/contacts/pending-item-templates/${templateId}/stages/${stageId}`, { method: 'DELETE' }),
        reorder: (templateId: string, ids: string[]) =>
          req<{ stages: TemplateStageRecord[] }>(`/contacts/pending-item-templates/${templateId}/stages/reorder`, { method: 'PUT', body: JSON.stringify({ ids }) }),
      },
      entries: {
        create: (templateId: string, body: { label: string; phase?: string | null; stageId?: string | null; role?: PendingItemTemplateRole; dueOffsetDays?: number | null }) =>
          req<PendingItemTemplateEntry>(`/contacts/pending-item-templates/${templateId}/entries`, { method: 'POST', body: JSON.stringify(body) }),
        update: (templateId: string, entryId: string, body: { label?: string; phase?: string | null; stageId?: string | null; role?: PendingItemTemplateRole; dueOffsetDays?: number | null }) =>
          req<PendingItemTemplateEntry>(`/contacts/pending-item-templates/${templateId}/entries/${entryId}`, { method: 'PATCH', body: JSON.stringify(body) }),
        remove: (templateId: string, entryId: string) =>
          req<{ ok: true }>(`/contacts/pending-item-templates/${templateId}/entries/${entryId}`, { method: 'DELETE' }),
        reorder: (templateId: string, entries: Array<{ id: string; phase: string | null; stageId?: string | null }>) =>
          req<{ entries: PendingItemTemplateEntry[] }>(`/contacts/pending-item-templates/${templateId}/entries/reorder`, { method: 'PUT', body: JSON.stringify({ entries }) }),
      },
    },
    interactions: {
      create: (contactId: string, body: { type: 'call' | 'whatsapp' | 'email' | 'note'; result: string; occurredAt?: string }) =>
        req<ContactTimelineEntry>(`/contacts/${contactId}/interactions`, { method: 'POST', body: JSON.stringify(body) }),
    },
    // CLI-24 — vista transversal de Operaciones: una fila por operación, no por persona.
    operations: {
      list: (params?: { search?: string; nextAction?: string; process?: string; attention?: boolean; includeStages?: boolean }) => {
        const query = new URLSearchParams();
        for (const [key, value] of Object.entries(params ?? {})) if (value) query.set(key, String(value));
        return req<OperationsListResponse>(`/contacts/operations${query.toString() ? `?${query}` : ''}`);
      },
    },
  },

  // HIP-03 — lo que vence avisa antes de vencer.
  expiryDates: {
    list: () => req<ExpiryDateRecord[]>('/expiry-dates'),
    create: (body: { label: string; anchorAt?: string; dueAt: string; recurrenceDays?: number | null; offsets?: number[]; responsibleUserId?: number | null }) =>
      req<ExpiryDateRecord>('/expiry-dates', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: { label?: string; anchorAt?: string; dueAt?: string; recurrenceDays?: number | null; offsets?: number[]; responsibleUserId?: number | null; status?: 'active' | 'retired' }) =>
      req<ExpiryDateRecord>(`/expiry-dates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    renew: (id: string) => req<ExpiryDateRecord>(`/expiry-dates/${id}/renew`, { method: 'POST' }),
  },

  analytics: {
    stats: () => req<VisitStats>('/analytics/stats'),
  },

  // Asistente del panel (Fase 2): telemetría del composer, ejecución de
  // acciones (con bitácora atómica en el servidor) y feed de actividad.
  assistant: {
    recordEvent: (body: AssistantEventInput) =>
      req<{ ok: boolean }>('/assistant/events', { method: 'POST', body: JSON.stringify(body) }),
    execute: (body: { intentId: string; param?: string }) =>
      req<AssistantExecuteResult>('/assistant/execute', { method: 'POST', body: JSON.stringify(body) }),
    activity: (limit = 8) => req<ActivityEntry[]>(`/assistant/activity?limit=${limit}`),
  },

  preview: {
    getUrl: (pageId: string) =>
      req<{ url: string }>(`/preview/${pageId}`, { method: 'POST' }),
    // Upsert the editor's unsaved block state for the live preview iframe.
    saveLive: (pageId: string, contentJson: string) =>
      req<{ ok: boolean }>(`/preview/live/${pageId}`, {
        method: 'PUT',
        body: JSON.stringify({ contentJson }),
      }),
    // Upsert the editor's unsaved site_settings (footer/nav/logos) for the live
    // preview iframe. Read by the site /preview?settings=1 route.
    saveLiveSettings: (settingsJson: string) =>
      req<{ ok: boolean }>(`/preview/live-settings`, {
        method: 'PUT',
        body: JSON.stringify({ settingsJson }),
      }),
  },

  // Generación de contenido con LLM (OpenRouter). El backend valida y limita por tenant.
  ai: {
    field: (collectionKey: string, fieldKey: string, opts?: { instruction?: string; itemContext?: Record<string, unknown> }) =>
      req<{ value: unknown }>('/ai/field', { method: 'POST', body: JSON.stringify({ collectionKey, fieldKey, ...opts }) }),
    item: (collectionKey: string, prompt?: string) =>
      req<{ data: Record<string, unknown> }>('/ai/item', { method: 'POST', body: JSON.stringify({ collectionKey, prompt }) }),
    bulkItems: (collectionKey: string, text: string, signal?: AbortSignal) =>
      req<{ data: Record<string, unknown>[] }>('/ai/bulk-items', { method: 'POST', body: JSON.stringify({ collectionKey, text }), signal }),
    seed: (collectionKey: string, count: number, businessContext?: string) =>
      req<{ created: number }>('/ai/seed', { method: 'POST', body: JSON.stringify({ collectionKey, count, businessContext }) }),
    block: (
      blockType: string,
      fieldName: string,
      opts?: {
        fieldLabel?: string;
        context?: string;
        locale?: string;
        instruction?: string;
        currentProps?: Record<string, unknown>;
        mode?: 'rewrite' | 'generate';
      },
    ) =>
      req<{ value: string }>('/ai/block', { method: 'POST', body: JSON.stringify({ blockType, fieldName, ...opts }) }),
    // Construye el sitio completo del tenant (Home + secciones + contacto + nav).
    site: (description?: string) =>
      req<{ pages: number }>('/ai/site', { method: 'POST', body: JSON.stringify({ description }) }),
    branding: (body: { instruction: string; industry?: string | null; currentTheme?: Record<string, unknown> }) =>
      req<{ theme: Record<string, unknown>; explanation: string; suggestionLabel: string }>(
        '/ai/branding',
        { method: 'POST', body: JSON.stringify(body) },
      ),
    designRecommendation: (body: { industry?: string | null; goal?: string; description?: string; zone?: string; specialty?: string; collections?: string[]; proposals?: Array<{ label: string; rationale: string }> }) =>
      req<{ recommendation: { proposalIndex: 0 | 1 | 2; emphasis: 'claridad' | 'confianza' | 'catalogo'; reason: string } | null }>('/ai/design-recommendation', { method: 'POST', body: JSON.stringify(body) }),
    image: (body: AiImageGenerateInput) =>
      req<AiImageGenerateResult>('/ai/image', { method: 'POST', body: JSON.stringify(body) }),
    // Fase 4: dispara la generación de imágenes de los bloques auto-elegibles del
    // sitio recién construido. No bloquea: el backend procesa en segundo plano.
    siteImages: () =>
      req<{ queued: number }>('/ai/site/images', { method: 'POST', body: JSON.stringify({}) }),
    siteImagesStatus: () =>
      req<{ pending: number; succeeded: number; failed: number; total: number }>('/ai/site/images/status'),
    // Cupo mensual de imágenes IA + si el plan lo incluye.
    imageUsage: () =>
      req<{ used: number; limit: number; month: string; allowed: boolean; plan: string | null }>('/ai/image/usage'),
    // Modo "Descríbela tú": preguntas de seguimiento para afinar antes de construir.
    pageQuestions: (pageName: string, description: string) =>
      req<{ questions: { question: string; options: string[] }[] }>(
        '/ai/page', { method: 'POST', body: JSON.stringify({ mode: 'describe', step: 'questions', pageName, description }) }),
    // Construye los bloques de una página (sin persistir). → contentJson.
    page: (opts: {
      mode: 'describe' | 'template';
      pageName: string;
      description?: string;
      templateKey?: string;
      answers?: { question: string; answer: string }[];
      bilingual?: boolean; // pide también la versión en inglés (contentJsonEn)
    }) =>
      req<{ contentJson: string; contentJsonEn?: string }>('/ai/page', { method: 'POST', body: JSON.stringify({ ...opts, step: 'build' }) }),
  },

  // Chatbot conversacional por tenant — config, uso y revisión de conversaciones.
  chatbot: {
    getSettings: () => req<ChatbotSettingsRecord>('/chatbot/settings'),
    updateSettings: (body: ChatbotSettingsInput) =>
      req<{ ok: boolean }>('/chatbot/settings', { method: 'PUT', body: JSON.stringify(body) }),
    usage: () => req<ChatbotUsageSummary>('/chatbot/usage'),
    conversations: () => req<ChatbotConversationSummary[]>('/chatbot/conversations'),
    conversation: (id: string) =>
      req<{ conversation: ChatbotConversationSummary; messages: ChatbotMessageRecord[] }>(`/chatbot/conversations/${id}`),
  },

  // Onboarding por industria — catálogo de presets + instanciación de tipos.
  onboarding: {
    catalog: () => req<OnboardingCatalog>('/onboarding/catalog'),
    // GAN-01: solo lo llama el wizard al montarse. Ver metricas-activacion.md §3.
    start: () => req<{ ok: boolean }>('/onboarding/start', { method: 'POST' }),
    designEvent: (body: { event: 'recommended' | 'selected' | 'changed'; recommendedIndex?: number; selectedIndex?: number }) =>
      req<{ ok: boolean }>('/onboarding/design-event', { method: 'POST', body: JSON.stringify(body) }),
    draft: {
      get: () => req<{
        businessName: string | null;
        siteDomain: string | null;
        draft: OnboardingDraft | null;
        paused: boolean;
      }>('/onboarding/draft'),
      save: (draft: OnboardingDraft, paused = false) =>
        req<{ ok: boolean }>('/onboarding/draft', {
          method: 'PUT',
          body: JSON.stringify({ draft, paused }),
        }),
    },
    importWebsite: (url: string, industry?: string | null) =>
      req<{ detected: DetectedIdentity }>('/onboarding/import-website', {
        method: 'POST',
        body: JSON.stringify({ url, industry: industry ?? undefined }),
      }),
    placesSearch: (query: string) =>
      req<{ results: GooglePlaceCandidate[] }>('/onboarding/places/search', {
        method: 'POST',
        body: JSON.stringify({ query }),
      }),
    placesImport: (placeId: string) =>
      req<{
        detected: DetectedIdentity;
        placeId: string;
        linked: { rating: number | null; reviews: number; amenities: number };
        replacesExisting: boolean;
      }>('/onboarding/places/import', {
        method: 'POST',
        body: JSON.stringify({ placeId }),
      }),
    placesConfirm: (placeId: string) =>
      req<{
        placeId: string;
        linked: { rating: number | null; reviews: number; amenities: number };
      }>('/onboarding/places/confirm', {
        method: 'POST',
        body: JSON.stringify({ placeId }),
      }),
    suggestPrompts: (body: {
      industry?: string | null;
      subvertical?: string | null;
      targetAreas?: string[];
      description?: string | null;
      locales?: Array<'es' | 'en'>;
    }) => req<{ suggestions: OnboardingPromptSuggestion[] }>('/onboarding/prompts/suggest', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
    applyPrompts: (prompts: OnboardingPromptSuggestion[]) =>
      req<{ created: number }>('/onboarding/prompts/apply', {
        method: 'POST',
        body: JSON.stringify({ prompts }),
      }),
    // industry opcional: el wizard lo fija; el catálogo "añadir contenido" lo omite.
    instantiate: (typeKeys: string[], industry?: string, proposalIndex = 0, proposalSeed?: string, paletteInput?: PaletteInput) =>
      req<{ created: string[] }>('/onboarding/types', { method: 'POST', body: JSON.stringify({ industry, typeKeys, proposalIndex, proposalSeed, paletteInput }) }),
    complete: () =>
      req<{
        ok: boolean;
        domain: string | null;
        publish: { ok: true; logId: number } | { ok: false; error: string };
      }>('/onboarding/complete', { method: 'POST' }),
    // ACQ-00 — importación de un resultado de las herramientas públicas. El canje
    // es el único punto donde el código de handoff se convierte en algo del tenant.
    acquisitionImport: {
      redeem: (code: string) =>
        req<{ ok: boolean; redemption: AcquisitionRedemption }>('/onboarding/acquisition-import/redeem', {
          method: 'POST',
          body: JSON.stringify({ code }),
        }),
      get: () => req<{ redemption: AcquisitionRedemption | null }>('/onboarding/acquisition-import'),
      apply: (redemptionId: string, fields: string[], assets: string[]) =>
        req<{ ok: boolean; appliedKeys: string[]; assets: string[] }>('/onboarding/acquisition-import/apply', {
          method: 'POST',
          body: JSON.stringify({ redemptionId, fields, assets }),
        }),
      dismiss: (redemptionId: string) =>
        req<{ ok: boolean }>('/onboarding/acquisition-import/dismiss', {
          method: 'POST',
          body: JSON.stringify({ redemptionId }),
        }),
    },
    checklist: () => req<ChecklistStatus>('/onboarding/checklist'),
    markChecklist: (id: string, completed: boolean) =>
      req<{ ok: boolean }>('/onboarding/checklist', { method: 'PUT', body: JSON.stringify({ id, completed }) }),
  },

  tenants: {
    list: () => req<TenantSummary[]>('/tenants'),
    search: (query: string) => req<TenantPickerOption[]>(`/tenants?q=${encodeURIComponent(query)}&limit=20`),
    resolve: (ids: string[]) => req<TenantPickerOption[]>(`/tenants?ids=${encodeURIComponent(ids.slice(0, 20).join(','))}&limit=20`),
  },

  identity: {
    get: () => req<BusinessIdentityRecord>('/identity'),
    save: (body: BusinessIdentityPatch) =>
      req<BusinessIdentityRecord>('/identity', { method: 'PUT', body: JSON.stringify(body) }),
  },

  locations: {
    cities: (query: string, sessionToken?: string) =>
      req<{ results: CitySuggestion[] }>('/locations/cities', {
        method: 'POST',
        body: JSON.stringify({ query, sessionToken }),
      }),
  },

  presence: {
    score: () => req<PresenceScoreResponse>('/presence/score'),
    markSignal: (id: string, completed: boolean) =>
      req<{ ok: boolean }>('/presence/signal', { method: 'PUT', body: JSON.stringify({ id, completed }) }),
    // HUB-02 — "Ahora no" en Inicio: oculta la sugerencia un tiempo (restore la devuelve).
    dismissSuggestion: (id: string, restore = false) =>
      req<{ ok: boolean }>('/presence/dismiss', { method: 'PUT', body: JSON.stringify({ id, restore }) }),
  },

  // Dashboard de Visibilidad Local — perfil, prompts, competidores, corridas y dashboard.
  visibility: {
    getProfile: () => req<BusinessProfileResponse>('/visibility/profile'),
    saveProfile: (body: Partial<BusinessProfileRecord> & { targetAreas?: string[] }) =>
      req<{ profile: BusinessProfileRecord }>('/visibility/profile', { method: 'PUT', body: JSON.stringify(body) }),

    attributes: () => req<{ attributes: BusinessAttributeRecord[] }>('/visibility/attributes'),
    createAttribute: (body: { key: string; value?: string | null; source?: string; isPrimary?: boolean }) =>
      req<{ attribute: BusinessAttributeRecord }>('/visibility/attributes', { method: 'POST', body: JSON.stringify(body) }),
    updateAttribute: (id: string, body: Partial<BusinessAttributeRecord>) =>
      req<{ attribute: BusinessAttributeRecord }>(`/visibility/attributes/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteAttribute: (id: string) => req<{ ok: boolean }>(`/visibility/attributes/${id}`, { method: 'DELETE' }),

    prompts: (status?: PromptStatus) =>
      req<{ prompts: TrackedPromptRecord[] }>(`/visibility/prompts${status ? `?status=${status}` : ''}`),
    createPrompt: (body: Partial<TrackedPromptRecord> & { promptText: string }) =>
      req<{ prompt: TrackedPromptRecord }>('/visibility/prompts', { method: 'POST', body: JSON.stringify(body) }),
    updatePrompt: (id: string, body: Partial<TrackedPromptRecord>) =>
      req<{ prompt: TrackedPromptRecord }>(`/visibility/prompts/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deletePrompt: (id: string) => req<{ ok: boolean }>(`/visibility/prompts/${id}`, { method: 'DELETE' }),

    competitors: (status?: CompetitorStatus) =>
      req<{ competitors: TrackedCompetitorRecord[] }>(`/visibility/competitors${status ? `?status=${status}` : ''}`),
    createCompetitor: (body: Partial<TrackedCompetitorRecord> & { name: string }) =>
      req<{ competitor: TrackedCompetitorRecord }>('/visibility/competitors', { method: 'POST', body: JSON.stringify(body) }),
    updateCompetitor: (id: string, body: Partial<TrackedCompetitorRecord>) =>
      req<{ competitor: TrackedCompetitorRecord }>(`/visibility/competitors/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteCompetitor: (id: string) => req<{ ok: boolean }>(`/visibility/competitors/${id}`, { method: 'DELETE' }),

    seeds: () => req<{ seeds: VisibilitySeedCatalogItem[] }>('/visibility/seeds'),
    seedDetail: (subvertical: string, zona?: string) =>
      req<VisibilitySeedDetail>(`/visibility/seeds/${subvertical}${zona ? `?zona=${encodeURIComponent(zona)}` : ''}`),
    applySeed: (subvertical: string, zona?: string) =>
      req<VisibilitySeedResult>('/visibility/seed', { method: 'POST', body: JSON.stringify({ subvertical, zona }) }),

    runs: () => req<{ runs: VisibilityRunRecord[] }>('/visibility/runs'),
    run: (id: string) => req<VisibilityRunDetail>(`/visibility/runs/${id}`),
    createRun: (body: { runType: RunType; operator?: string; notes?: string }) =>
      req<{ run: VisibilityRunRecord }>('/visibility/runs', { method: 'POST', body: JSON.stringify(body) }),
    updateRun: (id: string, body: { notes?: string; operator?: string; completed?: boolean }) =>
      req<{ run: VisibilityRunRecord }>(`/visibility/runs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteRun: (id: string) => req<{ ok: boolean }>(`/visibility/runs/${id}`, { method: 'DELETE' }),

    createObservation: (runId: string, body: {
      promptId: string;
      platform: VisibilityPlatform;
      tenantAppeared?: boolean;
      tenantPosition?: number | null;
      totalCited?: number;
      mentionLocation: VisibilityMentionLocation;
      responseStage: VisibilityResponseStage;
      accuracy: VisibilityAccuracy;
      accuracyIssueType?: VisibilityAccuracyIssueType | null;
      accuracyNote?: string | null;
      evidenceUrl?: string | null;
      evidenceNote?: string | null;
      citations?: CitationInput[];
    }) => req<{ observation: VisibilityObservationRecord }>(`/visibility/runs/${runId}/observations`, { method: 'POST', body: JSON.stringify(body) }),
    updateObservation: (id: string, body: Partial<{
      platform: VisibilityPlatform;
      tenantAppeared: boolean;
      tenantPosition: number | null;
      totalCited: number;
      mentionLocation?: VisibilityMentionLocation;
      responseStage?: VisibilityResponseStage;
      accuracy?: VisibilityAccuracy;
      accuracyIssueType?: VisibilityAccuracyIssueType | null;
      accuracyNote?: string | null;
      evidenceUrl: string | null;
      evidenceNote: string | null;
      citations: CitationInput[];
    }>) => req<{ observation: VisibilityObservationRecord }>(`/visibility/observations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteObservation: (id: string) => req<{ ok: boolean }>(`/visibility/observations/${id}`, { method: 'DELETE' }),
    promoteCitation: (id: string) =>
      req<{ competitor: TrackedCompetitorRecord; citation: VisibilityCitationRecord }>(`/visibility/citations/${id}/promote`, { method: 'POST' }),

    dashboard: () => req<VisibilityDashboard>('/visibility/dashboard'),
  },

  setup: {
    check: () => fetch(`${BASE}/setup`, { credentials: 'include' }).then((r) => r.json()) as Promise<SetupStatus>,
    create: (email: string, password: string, tenantName: string, tenantSlug: string, firstName: string, lastName: string, phone: string) =>
      req('/setup', { method: 'POST', body: JSON.stringify({ email, password, tenantName, tenantSlug, firstName, lastName, phone }) }),
  },

  auth: {
    login: (email: string, password: string) => {
      clearActiveTenantId();
      return authReq('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
    },
    // Magic link: pide un enlace de acceso por correo. Responde siempre ok (sin
    // revelar si el correo existe). En dev devuelve `devLink` para probar sin correo.
    requestMagicLink: (email: string) => {
      clearActiveTenantId();
      return authReq<{ ok: true; devLink?: string }>(
        '/auth/magic/request', { method: 'POST', body: JSON.stringify({ email }) },
      );
    },
    // Alta self-service: crea tenant + owner en estado pendiente de activar (GOL-07,
    // 2026-08-07 — ya NO deja la sesión iniciada). Respuesta idéntica exista o no ya
    // una cuenta con ese email: solo `{ ok: true }`, para no poder usarla como
    // oráculo de enumeración. `devLink` solo aparece en dev/sin correo configurado.
    // `slug`: el subdominio elegido y confirmado disponible vía `checkSlug` (opcional
    // por compatibilidad — sin él, el servidor lo deriva del nombre del negocio).
    // `acceptedLegal`: solo se exige si hay una versión publicada de Términos/Privacidad
    // (ver `terms.current` — si viene `null` el checkbox ni siquiera se muestra).
    signup: (email: string, password: string, businessName: string, firstName: string, lastName: string, phone?: string, slug?: string, acceptedLegal?: boolean) => {
      clearActiveTenantId();
      return authReq<{ ok: true; devLink?: string }>(
        '/signup', { method: 'POST', body: JSON.stringify({ email, password, businessName, firstName, lastName, phone, slug, acceptedLegal }) },
      );
    },
    // Disponibilidad de subdominio (<slug>.destakia.com) en tiempo real — público,
    // reusado por SignupPage y por el modal "Crear tenant" de Infraestructura (GAN-06).
    checkSlug: (slug: string) =>
      authReq<{ slug: string; available: boolean; reason?: 'invalid' | 'reserved' | 'taken'; suggestions: string[] }>(
        `/signup/check-slug?slug=${encodeURIComponent(slug)}`,
      ),
    resendSignupActivation: (email: string) =>
      authReq<{ ok: true; devLink?: string }>('/signup/resend-activation', {
        method: 'POST', body: JSON.stringify({ email }),
      }),
    logout: () => {
      clearActiveTenantId();
      return authReq('/auth/logout', { method: 'POST' });
    },
    me: async () => {
      const activeTenantId = getActiveTenantId();
      const suffix = activeTenantId ? `?tenantId=${encodeURIComponent(activeTenantId)}` : '';
      const data = await fetch(`${BASE}/auth/me${suffix}`, { credentials: 'include' }).then((r) => r.json()) as {
        user: SessionUser | null;
        needsOnboarding?: boolean;
        onboardingPaused?: boolean;
        onboardedAt?: number | null;
        pendingLegalAcceptance?: boolean;
      };
      if (!data.user || data.user.role !== 'saas_admin') clearActiveTenantId();
      return data;
    },
    changePassword: (currentPassword: string, newPassword: string) =>
      req('/auth/password', { method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }) }),
    requestPasswordReset: (email: string) => authReq<{ ok: true; devLink?: string }>('/auth/password-reset/request', { method: 'POST', body: JSON.stringify({ email }) }),
    requestEmailChange: (email: string) => req<{ ok: true; devLink?: string }>('/auth/email-change/request', { method: 'POST', body: JSON.stringify({ email }) }),
    accountActionContext: (token: string) =>
      authReq<{ purpose: 'invite' | 'password_reset' | 'email_change' | 'signup_verify' }>(`/auth/action/context?token=${encodeURIComponent(token)}`),
    completeAccountAction: (token: string, password?: string) => authReq<{ ok: true; purpose: string }>('/auth/action/complete', { method: 'POST', body: JSON.stringify({ token, password }) }),
  },

  platformTeam: {
    list: () => req<User[]>('/platform/team'),
    invite: (body: { email: string; isPlatformOwner?: boolean; firstName: string; lastName: string; phone: string }) => req<User>('/platform/team', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: { isPlatformOwner: boolean }) => req<User>(`/platform/team/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  },

  // Términos/Aviso de Privacidad vigentes de la plataforma — público (SignupPage,
  // LegalPage, TermsGateScreen) + aceptación del usuario en sesión.
  terms: {
    current: () => req<{ terms: PlatformLegalDoc | null; privacy: PlatformLegalDoc | null }>('/terms/current'),
    accept: () => req<{ ok: true }>('/terms/accept', { method: 'POST' }),
  },

  // Suscripción / muro de pago del sitio (tenant).
  billing: {
    entitlement: () => req<EntitlementResponse>('/billing/entitlement'),
    checkout: (planId?: string, interval?: 'month' | 'year') =>
      req<{ url: string }>('/billing/checkout', { method: 'POST', body: JSON.stringify({ planId, interval }) }),
    portal: () => req<{ url: string }>('/billing/portal', { method: 'POST' }),
    // Facturas a nombre del cliente (GOL-10B).
    invoices: () => req<TenantInvoicesResponse>('/billing/invoices'),
    saveFiscalProfile: (profile: FiscalProfileInput) =>
      req<{ ok: boolean; profile: FiscalProfile }>('/billing/fiscal-profile', {
        method: 'PUT',
        body: JSON.stringify(profile),
      }),
    requestInvoice: (recordId: string) =>
      req<{ ok: boolean; charge: InvoiceableCharge }>(`/billing/invoices/${encodeURIComponent(recordId)}/request`, {
        method: 'POST',
      }),
    // El archivo lo sirve el panel, no el PAC: sus URLs exigen la clave del
    // emisor. Por eso es descarga a blob y no un `<a href>` al proveedor.
    downloadInvoice: (recordId: string, format: 'pdf' | 'xml', filename: string) =>
      downloadFile(`/billing/invoices/${encodeURIComponent(recordId)}/download/${format}`, `${filename}.${format}`),
  },

  // Gobierno de herramientas del dueño del SaaS (solo saas_admin).
  tools: {
    list: () => req<PluginToolsResponse>('/tools'),
    export: (id: string) => req<Record<string, unknown>>(`/tools/${encodeURIComponent(id)}/export`),
    install: (pluginId: string, version: string) => req<{ id: string }>(`/tools/install/${encodeURIComponent(pluginId)}`, { method: 'POST', body: JSON.stringify({ version }) }),
    change: (id: string, action: string, body: Record<string, unknown>) => req<{ ok: boolean; revision: number }>(`/tools/${encodeURIComponent(id)}/${action}`, { method: 'POST', body: JSON.stringify(body) }),
    test: (id: string, body: Record<string, unknown>) => req<MortgageEstimate | SaleProceedsEstimate>(`/tools/${encodeURIComponent(id)}/test`, { method: 'POST', body: JSON.stringify(body) }),
    placements: (id: string, revision: number, placements: { pageId: string; position: number }[]) => req<{ ok: boolean; revision: number }>(`/tools/${encodeURIComponent(id)}/placements`, { method: 'PUT', body: JSON.stringify({ revision, placements }) }),
  },
  platformPlugins: {
    list: () => req<{ items: PluginOwnerItem[] }>('/platform/plugins'),
    impact: (id: string, policy: PluginPolicy) => req<{ impact: PluginOwnerItem['impact'] }>(`/platform/plugins/${encodeURIComponent(id)}/impact`, { method: 'POST', body: JSON.stringify({ policy }) }),
    save: (id: string, policy: PluginPolicy, revision: number, reason: string) => req<{ ok: boolean; governance: PluginGovernance; impact: PluginOwnerItem['impact'] }>(`/platform/plugins/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ policy, revision, reason }) }),
  },
  platformDatasets: {
    list: () => req<{ items: (CalculationDatasetRelease & { revision: number })[] }>('/platform/datasets'),
    update: (datasetId: string, version: string, body: { revision: number; status: 'internal' | 'published' | 'disabled'; licenseEvidence?: string; disabledReason?: string }) =>
      req<{ ok: boolean; revision: number; status: string }>(`/platform/datasets/${encodeURIComponent(datasetId)}/${encodeURIComponent(version)}`, { method: 'PUT', body: JSON.stringify(body) }),
  },
  propertyComparables: {
    list: () => req<{ items: PropertyComparable[] }>('/property-comparables'),
    replace: (rows: PropertyComparable[]) => req<{ ok: boolean; imported: number }>('/property-comparables', { method: 'PUT', body: JSON.stringify({ rows }) }),
  },
  propertyMarketReferences: {
    list: () => req<{ items: PropertyMarketReference[] }>('/property-market-references'),
    geo: (state?: string, municipality?: string) => req<{ states?: { code: string; name: string }[]; municipalities?: { code: string; name: string }[]; settlements?: { code: string; name: string }[] }>(`/property-market-references/geo${state ? `?state=${encodeURIComponent(state)}${municipality ? `&municipality=${encodeURIComponent(municipality)}` : ''}` : ''}`),
    create: (body: Record<string, unknown>) => req<{ ok: boolean; id: string }>('/property-market-references', { method: 'POST', body: JSON.stringify(body) }),
    disable: (id: string) => req<{ ok: boolean }>(`/property-market-references/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  },
  // Configuración de billing del dueño del SaaS (solo saas_admin).
  platformBilling: {
    get: () => req<PlatformBillingResponse>('/platform/billing'),
    stripePrices: () => req<{ available: boolean; prices: StripePriceOptionState[] }>('/platform/billing/stripe-prices'),
    saveGate: (gate: BillingGateState) =>
      req<{ ok: boolean; gate: BillingGateState }>('/platform/billing/gate', { method: 'PUT', body: JSON.stringify(gate) }),
    saveCatalog: (catalog: Record<string, PlanDefinitionState>) =>
      req<{ ok: boolean; catalog: Record<string, PlanDefinitionState> }>('/platform/billing/catalog', { method: 'PUT', body: JSON.stringify(catalog) }),
    saveAddons: (addons: Record<string, AddonDefinitionState>) =>
      req<{ ok: boolean; addons: Record<string, AddonDefinitionState> }>('/platform/billing/addons', { method: 'PUT', body: JSON.stringify(addons) }),
    saveRollout: (rollout: Record<string, FeatureRolloutState>) =>
      req<{ ok: boolean; rollout: Record<string, FeatureRolloutState> }>('/platform/billing/rollout', { method: 'PUT', body: JSON.stringify(rollout) }),
    runDecommission: (dryRun = true) =>
      req<DecommissionReportState>(`/platform/billing/decommission?dryRun=${dryRun}`, { method: 'POST' }),
  },

  // Configuración fiscal y seguimiento mensual del dueño (CFDI/RESICO; solo saas_admin).
  platformFiscal: {
    get: () => req<PlatformFiscalResponse>('/platform/fiscal'),
    saveConfig: (config: FiscalConfigState) =>
      req<{ ok: boolean; config: FiscalConfigState }>('/platform/fiscal/config', { method: 'PUT', body: JSON.stringify(config) }),
    report: (period?: string) =>
      req<MonthlyReportState>(`/platform/fiscal/report${period ? `?period=${period}` : ''}`),
    markRecord: (id: string, patch: { cfdiStatus?: string; cfdiUuid?: string; note?: string }) =>
      req<{ ok: boolean }>(`/platform/fiscal/records/${id}/mark`, { method: 'POST', body: JSON.stringify(patch) }),
    // GOL-10A — cierre mensual.
    closure: (period: string) =>
      req<FiscalClosurePreview>(`/platform/fiscal/closure?period=${period}`),
    runClosure: (period: string, force = false) =>
      req<FiscalClosureRunResult>('/platform/fiscal/closure/run', { method: 'POST', body: JSON.stringify({ period, force }) }),
    // GOL-10B — solicitudes de factura a nombre de clientes (auditoría).
    invoiceRequests: () =>
      req<{ requests: InvoiceRequestState[] }>('/platform/fiscal/invoice-requests'),
    downloadClosure: (period: string, format: 'pdf' | 'xml') =>
      downloadFile(`/platform/fiscal/closure/${period}/download/${format}`, `factura-global-${period}.${format}`),
    // Descarga de texto (CSV) — no pasa por `req`, que espera JSON.
    downloadReport: async (period: string): Promise<void> => {
      const res = await fetch(withTenantSuffix(`/platform/fiscal/report?period=${period}&format=csv`));
      if (!res.ok) throw new Error('No se pudo preparar el archivo del mes. Intenta de nuevo.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ingresos-${period}.csv`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    },
  },

  // Configuración de los Términos/Aviso de Privacidad de la plataforma + registro de
  // aceptación (solo saas_admin).
  platformLegal: {
    get: () => req<{ settings: LegalSettings; readiness: LegalReadiness; inventoryPendingCount: number }>('/platform/legal'),
    save: (body: { terms?: { version: string; content: string }; privacy?: { version: string; content: string } }) =>
      req<{ ok: boolean; settings: LegalSettings }>('/platform/legal', { method: 'PUT', body: JSON.stringify(body) }),
    acceptances: () => req<{ rows: LegalAcceptanceRow[] }>('/platform/legal/acceptances'),
    versions: (docKey: 'terms' | 'privacy') =>
      req<{ versions: LegalDocumentVersionRow[] }>(`/platform/legal/versions?docKey=${docKey}`),
    // LEG-04 — perfil, operación/retención y borradores de Destakia como responsable.
    saveProfile: (profile: Partial<LegalProfile>) =>
      req<{ profile: LegalProfile }>('/platform/legal/profile', { method: 'PUT', body: JSON.stringify(profile) }),
    saveOperational: (body: {
      operational?: Partial<Omit<LegalOperationalSettings, 'updatedAt' | 'updatedByEmail'>>;
      retention?: { key: RetentionRule['key']; retentionDays: number; note?: string | null }[];
    }) => req<{ operational: LegalOperationalSettings; retention: RetentionRule[] }>(
      '/platform/legal/operational', { method: 'PUT', body: JSON.stringify(body) },
    ),
    generateDraft: (docKey: 'terms' | 'privacy') =>
      req<{ content: string }>('/platform/legal/drafts/generate', { method: 'POST', body: JSON.stringify({ docKey }) }),
    saveDrafts: (body: { terms?: { content: string }; privacy?: { content: string } }) =>
      req<{ drafts: LegalDrafts }>('/platform/legal/drafts', { method: 'PUT', body: JSON.stringify(body) }),
    syncCookiesPage: () => req<{ ok: boolean }>('/platform/legal/cookies-page/sync', { method: 'POST' }),
  },

  // Inventario de proveedores, cookies y rastreo (LEG-07) — solo saas_admin.
  platformLegalInventory: {
    list: () => req<{ items: LegalInventoryItem[]; pendingCount: number }>('/platform/legal/inventory'),
    history: (key: string) => req<{ changes: LegalInventoryChange[] }>(`/platform/legal/inventory/${key}/history`),
    save: (key: string, input: LegalInventoryItemInput, status: LegalInventoryStatus) =>
      req<{ item: LegalInventoryItem }>(`/platform/legal/inventory/${key}`, {
        method: 'PUT', body: JSON.stringify({ ...input, status }),
      }),
    markReviewed: (key: string) =>
      req<{ item: LegalInventoryItem }>(`/platform/legal/inventory/${key}/review`, { method: 'POST' }),
  },

  // GOL-06 — `Ajustes → Privacidad y datos` del tenant admin.
  arco: {
    list: () => req<{ requests: ArcoRequest[] }>('/arco/requests'),
    create: (input: ArcoRequestInput & { confirmSlug?: string }) =>
      req<{ request: ArcoRequest }>('/arco/requests', { method: 'POST', body: JSON.stringify(input) }),
    withdraw: (id: string) => req<{ request: ArcoRequest }>(`/arco/requests/${id}/withdraw`, { method: 'POST' }),
    // Descarga binaria (ZIP) — no pasa por `req` (JSON). El nombre de archivo viene
    // del Content-Disposition que arma el worker.
    downloadExport: async (id: string): Promise<void> => {
      const res = await fetch(withTenantSuffix(`/arco/export/${id}/download`));
      if (!res.ok) throw new Error('No se pudo generar tu exportación. Intenta de nuevo en unos minutos.');
      const blob = await res.blob();
      const match = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = match?.[1] ?? 'destakia-datos.zip';
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    },
  },

  // GOL-06 — cola de revisión ARCO de todos los tenants. Solo saas_admin.
  platformArco: {
    list: (filters: { status?: ArcoRequestStatus; type?: ArcoRequestType } = {}) => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.type) params.set('type', filters.type);
      const qs = params.toString();
      return req<{ requests: (ArcoRequest & { tenant: { id: string; slug: string; name: string } | null })[] }>(
        `/platform/arco/requests${qs ? `?${qs}` : ''}`,
      );
    },
    decide: (id: string, body: { status: 'in_review' | 'needs_info' | 'resolved' | 'denied'; decision?: string }) =>
      req<{ request: ArcoRequest }>(`/platform/arco/requests/${id}/decide`, { method: 'POST', body: JSON.stringify(body) }),
    purgeNow: (id: string) => req<{ ok: boolean; leaks: Record<string, number> }>(`/platform/arco/requests/${id}/purge-now`, { method: 'POST' }),
  },

  // GOL-23 — registro mínimo de incidentes de seguridad/privacidad. Solo saas_admin.
  platformSecurityIncidents: {
    list: () => req<{ incidents: SecurityIncident[] }>('/platform/security-incidents'),
    create: (body: { occurredAt?: string; description: string; scope: string }) =>
      req<{ incident: SecurityIncident }>('/platform/security-incidents', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: {
      notificationDecision?: NotificationDecision;
      notifiedNote?: string;
      markNotified?: boolean;
      close?: boolean;
      closedNote?: string;
    }) => req<{ incident: SecurityIncident }>(`/platform/security-incidents/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  },

  // Centro de notificaciones in-app (COMM-02): lectura del usuario autenticado.
  notifications: {
    list: (before?: number) =>
      req<{ items: NotificationItem[]; unreadCount: number }>(`/notifications${before ? `?before=${before}` : ''}`),
    markRead: (id: string) => req<{ ok: boolean }>(`/notifications/${id}/read`, { method: 'POST' }),
    markAllRead: () => req<{ ok: boolean }>('/notifications/read-all', { method: 'POST' }),
  },

  // Compose de avisos a tenants / equipo Destakia (COMM-02). Solo saas_admin.
  platformNotifications: {
    audiencePreview: (mode: TenantAudienceMode, value?: string) =>
      req<{ count: number; tenants: { id: string; name: string }[] }>(
        `/platform/notifications/audience-preview?mode=${mode}${value ? `&value=${encodeURIComponent(value)}` : ''}`,
      ),
    broadcast: (body: BroadcastInput) =>
      req<{ notified: number; email?: { sent: number; suppressed: number; failed: number } }>(
        '/platform/notifications/broadcast', { method: 'POST', body: JSON.stringify(body) },
      ),
  },

  platformMetrics: {
    // GAN-01 — momento mágico. `medianMinutes` es null mientras no haya suficientes casos
    // (`minSample`); la UI nunca debe leer eso como "cero minutos".
    get: () => req<{
      magicMoment: {
        medianMinutes: number | null;
        reached: number;
        started: number;
        windowDays: number;
        minSample: number;
      };
      // ACQ-00 — embudo de las herramientas públicas subsidiadas (30 días).
      acquisition: AcquisitionFunnel;
    }>('/platform/metrics'),
    // Corta un enlace de reporte antes de su vencimiento. Deja traza de plataforma.
    revokeAcquisitionClaim: (runId: string) =>
      req<{ ok: boolean }>('/platform/metrics/acquisition/revoke', {
        method: 'POST',
        body: JSON.stringify({ runId }),
      }),
  },

  // OPS-05A — costo unitario de cada operación y costo por tenant/mes. El dinero
  // llega ya calculado en MXN; la UI no rehace ninguna cuenta.
  platformCosts: {
    get: (month?: string) => req<PlatformCostsResponse>(
      `/platform/costs${month ? `?month=${month}` : ''}`,
    ),
  },

  platformTenants: {
    list: () => req<TenantSummary[]>('/tenants'),
    create: (body: {
      name: string;
      slug?: string;
      tenantType: TenantType;
      industry?: string | null;
      locales?: string[];
    }) => req<{
      id: string;
      name: string;
      slug: string;
      tenantType: TenantType;
      industry: string | null;
      ownerUserId: number | null;
    }>('/platform/tenants', { method: 'POST', body: JSON.stringify(body) }),
    update: (tenantId: string, body: { tenantType?: TenantType; industry?: string | null }) =>
      req<{
        id: string;
        name: string;
        slug: string;
        tenantType: TenantType;
        industry: string | null;
        ownerUserId: number | null;
      }>(`/platform/tenants/${tenantId}`, { method: 'PUT', body: JSON.stringify(body) }),
    listUsers: (tenantId: string) => req<User[]>(`/platform/tenants/${tenantId}/users`),
    assignOwner: (tenantId: string, body: { userId?: number; email?: string; password?: string; firstName?: string; lastName?: string; phone?: string }) =>
      req<{
        ok: boolean;
        tenant: { id: string; name: string; ownerUserId: number | null };
        owner: User;
        created: boolean;
      }>(`/platform/tenants/${tenantId}/owner`, { method: 'POST', body: JSON.stringify(body) }),
    clearOwner: (tenantId: string) => req<{ ok: boolean }>(`/platform/tenants/${tenantId}/owner`, { method: 'DELETE' }),
    updateUserRole: (tenantId: string, userId: number, role: 'admin' | 'editor') =>
      req<User>(`/platform/tenants/${tenantId}/users/${userId}/role`, { method: 'PUT', body: JSON.stringify({ role }) }),
    getBillingStatus: (tenantId: string) =>
      req<{ status: string; active: boolean; trialDaysLeft: number | null; plan: string }>(`/platform/tenants/${tenantId}/billing`),
    pauseAccess: (tenantId: string) =>
      req<{ ok: boolean; status: string }>(`/platform/tenants/${tenantId}/billing/pause`, { method: 'POST' }),
    unpauseAccess: (tenantId: string) =>
      req<{ ok: boolean; status: string }>(`/platform/tenants/${tenantId}/billing/unpause`, { method: 'POST' }),
    grantCourtesy: (tenantId: string, planId: string) =>
      req<{ ok: boolean; status: string; planId: string; gateway: string }>(`/platform/tenants/${tenantId}/billing/courtesy`, { method: 'POST', body: JSON.stringify({ planId }) }),
    getSiteStatus: (tenantId: string) =>
      req<{ paused: boolean; hasDomain: boolean; domainMode: string | null }>(`/platform/tenants/${tenantId}/site-status`),
    pauseSite: (tenantId: string) =>
      req<{ ok: boolean; paused: boolean }>(`/platform/tenants/${tenantId}/site/pause`, { method: 'POST' }),
    unpauseSite: (tenantId: string) =>
      req<{ ok: boolean; paused: boolean }>(`/platform/tenants/${tenantId}/site/unpause`, { method: 'POST' }),
    publishLog: () => req<PlatformPublishLogEntry[]>('/platform/tenants/publish-log'),
    cloneToTest: (sourceTenantId: string, body: { purpose: string; name?: string; slug?: string; idempotencyKey?: string; noticeEmail?: string }) =>
      req<{ operation: TenantOperation; targetTenant: { id: string; slug: string; name: string; domain: string } }>(`/platform/tenants/${sourceTenantId}/clone-to-test`, { method: 'POST', body: JSON.stringify(body) }),
    operation: (operationId: string) => req<{ operation: TenantOperation; targetTenant: TenantSummary | null; failureDetail: string | null }>(`/platform/tenants/operations/${operationId}`),
    deleteInternalTest: (tenantId: string, confirmation: string) =>
      req<{ operation: TenantOperation }>(`/platform/tenants/${tenantId}`, { method: 'DELETE', body: JSON.stringify({ confirmation }) }),
  },

  platformSignup: {
    get: () => req<{ settings: SignupGateState; entries: SignupAccessEntry[]; campaigns: PlatformWaitlistCampaignOption[]; waitlist: PlatformWaitlistPreview }>('/platform/signup'),
    save: (settings: SignupGateState) =>
      req<{ ok: boolean; settings: SignupGateState }>('/platform/signup', { method: 'PUT', body: JSON.stringify(settings) }),
    waitlistPreview: (campaign?: string | null) =>
      req<PlatformWaitlistPreview>(`/platform/signup/waitlist/preview${campaign ? `?campaign=${encodeURIComponent(campaign)}` : ''}`),
    bootstrapWaitlist: () =>
      req<{ ok: boolean; campaign: PlatformWaitlistCampaignOption }>('/platform/signup/waitlist/bootstrap', { method: 'POST' }),
    notifyWaitlist: () =>
      req<{ ok: boolean; sent: number; failed: number; suppressed: number; paused: boolean }>('/platform/signup/waitlist/notify', { method: 'POST' }),
    addAllowlist: (email: string, note?: string) =>
      req<{ ok: boolean; entry: SignupAccessEntry }>('/platform/signup/allowlist', { method: 'POST', body: JSON.stringify({ email, note }) }),
    removeAllowlist: (email: string) =>
      req<{ ok: boolean }>(`/platform/signup/allowlist/${encodeURIComponent(email)}`, { method: 'DELETE' }),
  },
};
